import { APP_VERSION, CONFIG, tg, state, log, show, finish, toast, initHeaderToggles, applyLockerCtx, setOperationActive, showBackendDownOverlay, showUpstreamErrorOverlay, hideUpstreamErrorOverlay, createInlineAlert } from './ui.js';
import { api, runWithLoader, updateLoaderText, hasCourierIdentity } from './api.js';
import { showMaintenanceOverlay, hideMaintenanceOverlay } from './ui.js';
import { isEpAgent, loadParcels, guessDeliveryParcel, scanMissMessage, nextSizeForAgent } from './flows.js';
import {
  DEFAULT_CELL_SIZE, normalizeFreeCells, freeCellsView, decrementFreeCell, isSizeBlocked,
  markCellSizeEmpty, markFreeCellsStale, freeCellsSummary, freeCellsActualLabel,
  freeCellsTotal, firstOpenableSize, anyOpenableSize, isTerminalFreeCellsStatus, isFreeCellsOffline,
  LOCKER_CODE_NO_FREE_CELLS, LOCKER_CODE_NOT_OPENED, FREE_CELLS_MANUAL_REFRESH_MS,
  isCellResizeRejected, allowsAutoSizeEscalation, LOCKER_TEXT_1C_FAILURE,
  LOCKER_TEXT_1C_FAILURE_CONFIRM, CELL_RESIZE_REJECTED_TITLE, CELL_RESIZE_REJECTED_HINT,
  EPICENTR_SWAP_PROBLEM_TITLE, rememberCellResizeRejected, cellResizeRejectedSizes,
  cellResizeRejectedHint, suggestCellResizeSize, cellResizeRetryBlockedText,
} from './locker-cells.js';
import { LOCKER_CLIENT_APP_MIN1, parcelAgentForRequest } from './locker-marks.js';
import {
  EPICENTR_NETWORK_ID, EPICENTR_PARCEL_AGENT, epicentrBlockFor, epFirstListView, epFirstText,
  isEpicentrParcel, EPICENTR_BOOKED_OPEN_SIZE, noteHandledInVisit, forgetProblemInVisit, handledInVisit,
  epFirstStorageKey, kyivDay, browserStorage, restoreEpFirstVisits, persistEpFirstVisits,
  noteServerHandledInVisit,
} from './locker-ep-first.js';
import {
  createOnlinePoller, normalizeOnlineStatus, onlineCheckApplies, isKnownOffline,
  isBlindStatus, isFinalUnsupported, offlineBannerText, liveStatusLine, backOnlineAlert,
  ONLINE_STATUS_TIMEOUT_MS,
} from './postamat-online.js';
import { normalizeInventoryTask, inventoryTaskKey, buildInventoryPayload } from './inventory.helpers.js';
import { tsdActive, initTsdToggles, initTsdKeyboard, setTsdMode } from './tsd.js';
import { loadCourierSettings, saveCourierSettings } from './settings.js';
import { initSimulator, simulator } from './simulator.js';

// Signal that the ES module loaded successfully (used by fallback in HTML)
window.__appModuleLoaded = true;

// Register Service Worker for offline support
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw-locker.js', { scope: '/' })
    .then(reg => console.log('[SW-Locker] Registered:', reg.scope))
    .catch(err => console.warn('[SW-Locker] Registration failed:', err));
}

// Id критичного попередження «комірка могла відкритись»: за ним застосунок
// розуміє, що екран зараз перекривати не можна (див. isCellStepCritical).
const CELL_WARN_ALERT_ID = 'alert_cell_may_be_open';
// Місце плашок про комірку — під шапкою поштомата (app_min1.html), а не над
// нею: інакше шапка й сітка розмірів зʼїжджали вниз, а «Відкрити повторно» і
// «Проблема» йшли за межі екрана телефона (див. showCellAlert).
const CELL_ALERT_HOST_ID = 'cell_alert_host';
// Плашка «не вдалося змінити розмір броні» (13 від Епіцентру, warnCellResizeRejected).
// Окремий id, бо комірка тут НЕ відкривалась: крок не критичний, і
// isCellStepCritical її не рахує. Знімається там само, де й CELL_WARN (clearCellWarn).
const CELL_RESIZE_ALERT_ID = 'alert_cell_resize';

/**
 * Чи кур'єр зараз на критичному кроці з коміркою, який не можна перекривати
 * блокувальним екраном і не можна стирати перезавантаженням сторінки.
 *
 * state.operationActive сюди НЕ вистачає: його піднімає лише withOperation, а
 * той тримає прапорець рівно на час HTTP-запиту. Найнебезпечніший проміжок —
 * людський: між /locker/insert (комірка вже відкрита, transID отримано) і
 * /locker/insert/confirm кур'єр іде й кладе посилку, і весь цей час
 * operationActive === false. Сюди ж належить попередження «комірка могла
 * відкритись» після неоднозначного збою: воно критичне і має лишитись на
 * екрані, а не зникнути під оверлеєм чи після location.reload().
 */
function isCellStepCritical() {
  if (state.operationActive) return true;
  if (state.currentDeliveryTrans || state.currentPickupTrans) return true;
  const warn = document.getElementById(CELL_WARN_ALERT_ID);
  return Boolean(warn && !warn.classList.contains('inline-alert-hide'));
}

/**
 * Блокер «Сервер не відповідає» на екрані поштоматів.
 *
 * Рішення про показ і приховування віддане connectivity.js (класичний скрипт,
 * глобал window.MeestConnectivity): кілька спроб із таймаутами замість одного
 * fetch, фонове самовідновлення і окремий текст для «немає інтернету».
 * Тут лишається тільки те, що знає саме цей екран: техвікно, критичний крок із
 * коміркою і що робити після відновлення звʼязку.
 *
 * Ідемпотентна: init() повертає вже створений контролер.
 */
function initConnectivity() {
  const conn = typeof window !== 'undefined' ? window.MeestConnectivity : null;
  if (!conn || typeof conn.init !== 'function') return null;
  return conn.init({
    // Техвікно має пріоритет — два блокувальні екрани одночасно не показуємо.
    isMaintenanceVisible: () => Boolean(state.maintenance.visible || window.__maintenance?.visible),
    // Поки триває критичний крок із коміркою, показ відкладається: перекрити
    // момент підтвердження вкладення оверлеєм не можна (так само робить
    // maintenance.js).
    isOperationActive: isCellStepCritical,
    // Успішне зондування заразом приносить статус техвікна.
    onStatus: (st) => {
      if (st && st.active) {
        if (!state.operationActive) showMaintenanceOverlay(st.message, st.endsAt);
      } else if (state.maintenance.visible) {
        hideMaintenanceOverlay();
      }
    },
    // Блокер звʼязку зʼявився — прибираємо оверлей «помилка сервісу Meest»,
    // якщо його встиг намалювати 5xx тієї самої аварії: дві різні причини на
    // одну подію плутають кур'єра, а видно все одно лише верхній.
    onBlock: () => { hideUpstreamErrorOverlay(); },
    onRecover: (mode) => {
      state.errors.backendDownVisible = false;
      // Ручне «Спробувати знову» = явний намір почати з чистого аркуша. Але не
      // посеред критичного кроку з коміркою: перезавантаження стерло б і
      // попередження «не кладіть посилку», і transID, без якого вкладення вже
      // не підтвердити — посилка лишилась би у відкритій комірці.
      if (mode === 'manual' && !isCellStepCritical()) { location.reload(); return; }
      // Автовідновлення посеред роботи сторінку не перезавантажує: кур'єр міг
      // стояти посеред флоу, а стан живе в памʼяті.
      toast('Звʼязок відновлено');
    },
    log: (event, data) => log(event, data),
  });
}

let deliveryTotal = 0;
let scanInProgress = false;
let scanGuard = null;
let qrSupported = true;
let inventorySubmitting = false;

let lastTrainingMode = !!(state.settings && state.settings.trainingMode);

const AUTO_SCAN_MAX_ATTEMPTS = 4;
let autoScanTimer = null;
let autoScanAttempts = 0;
let autoScanPopupOpen = false;
let autoScanCompleted = false;

let tgReadyCalled = false;
let tgExpanded = false;
let tgExpandAttempts = 0;

function ensureWebAppReady() {
  if (tgReadyCalled) return;
  try {
    tg?.ready?.();
    tgReadyCalled = true;
  } catch (e) {
    log('tg:ready_fail ' + (e?.message || e));
    tgReadyCalled = true;
  }
}

function ensureWebAppExpanded() {
  ensureWebAppReady();
  if (tgExpanded || typeof tg?.expand !== 'function') return;
  try {
    tg.expand();
    tgExpanded = true;
  } catch (e) {
    if (tgExpandAttempts < 3) {
      log('tg:expand_fail ' + (e?.message || e));
    }
  } finally {
    tgExpandAttempts += 1;
  }
}

function autoOpenEnabled() {
  return !!(state.settings && state.settings.autoOpenCell);
}

function highlightSizeButton(size) {
  let matched = false;
  document.querySelectorAll('#size_selector .size-btn').forEach((btn) => {
    const key = btn.getAttribute('data-size');
    const active = key === size;
    btn.classList.toggle('primary', active);
    if (active) matched = true;
  });
  return matched;
}

// ─── Сітка розмірів комірок: чистий від мережі блок ─────────────────────────
// Вирізається тестами з цього файлу і виконується як є (той самий readFileSync-
// пін, що й для хелперів помилок нижче) — щоб перевірялась РЕАЛЬНА сітка, а не
// її копія в тесті.

/**
 * Малює сітку розмірів під поточні дані про вільні комірки.
 *
 * Два стани, і обидва мають бути однаково зрозумілі:
 *  • дані Є — під літерою розміру стоїть, скільки таких комірок вільно;
 *    розмір із нулем не натискається і видимо погашений;
 *  • даних НЕМАЄ (не епіцентрівський поштомат, метод не відповів) — сітка
 *    виглядає рівно як до 1.9.1: самі літери, жодних кількостей і жодних
 *    згадок про недоступність. Курʼєру байдуже, чому ми не знаємо.
 *
 * Третій стан — знімок після забору (див. markFreeCellsStale): нулі з нього
 * зникають разом із погашенням, бо комірки щойно звільнив сам курʼєр.
 *
 * Четвертий — вільних немає ЖОДНОГО розміру (1С відповіла `ok` з порожнім
 * переліком). Тоді кнопки гаснуть на вигляд, але лишаються натисними: усе,
 * що ми маємо на руках, — чужі дані, а курʼєр стоїть біля поштомата.
 */
function renderSizeGrid() {
  const view = freeCellsView(state.freeCells);
  document.querySelectorAll('#size_selector .size-btn').forEach((btn) => {
    const size = btn.getAttribute('data-size');
    const item = view.sizes.find((s) => s.size === size);
    if (!item) return;
    const freeEl = btn.querySelector('.size-btn-free');
    if (freeEl) freeEl.textContent = item.countLabel;
    // Гасимо вигляд за даними, а натисність — за правилом isSizeBlocked:
    // нуль в одного розміру кнопку блокує, нулі в усіх — ні. Посилку Епіцентру
    // з бронню нулі не стосуються: її комірка вже заброньована і серед вільних
    // не рахується (EPICENTR_BOOKED_OPEN_SIZE).
    btn.disabled = !state.currentDeliveryBooked && isSizeBlocked(view, size);
    btn.classList.toggle('size-btn-empty', item.disabled);
    btn.setAttribute('aria-label', item.ariaLabel);
  });
  return view;
}

/** Чи маємо ми взагалі дані про вільні комірки цього поштомата. */
function hasFreeCellData() {
  return Boolean(state.freeCells && state.freeCells.available);
}

/** Габарити комірки обраного розміру одним рядком, або null. */
function cellDimsForSize(size) {
  const item = freeCellsView(state.freeCells).sizes.find((s) => s.size === size);
  return item ? item.dims : null;
}

/**
 * 1С щойно відмовила через відсутність вільної комірки цього розміру — отже
 * наші дані про нього застаріли. Оновлюємо їх ЛОКАЛЬНО: це знання прийшло
 * безкоштовно, разом із відмовою, і перепитувати за ним поштомат не треба.
 */
function noteSizeRejected(lockerErr, size) {
  // Відмова по посилці Епіцентру з бронню — про її бронь («більшого розміру»),
  // а не про вільні комірки поштомата: гасити розмір для інших посилок не можна.
  if (state.currentDeliveryBooked) return;
  if (!lockerErr || String(lockerErr.code) !== LOCKER_CODE_NO_FREE_CELLS) return;
  if (!hasFreeCellData()) return;
  const updated = markCellSizeEmpty(state.freeCells, size);
  if (updated === state.freeCells) return;
  state.freeCells = updated;
  renderSizeGrid();
}

// ─── Кінець сітки розмірів комірок ──────────────────────────────────────────

//: Бюджет викликів. Meest ми пообіцяли ≈1 700 звернень на добу — один на візит
//: курʼєра до поштомата. Тому автоматично метод викликається РІВНО ОДИН раз,
//: після сканування QR, і ніколи після вкладення (вкладень 12-14 тисяч на добу,
//: це було б увосьмеро більше за обіцяне): після вкладення лічильник зменшуємо
//: самі, а після забору — позначаємо знімок застарілим (markFreeCellsStale),
//: теж без запиту. Ручне оновлення курʼєром — окремий шлях із власним
//: тротлом (FREE_CELLS_MANUAL_REFRESH_MS у locker-cells.js).

/**
 * Запитати вільні комірки за розмірами. Окремо від сканування QR: екран має
 * намалюватись одразу, а довідка доїхати за ~400 мс після нього.
 *
 * Мовчазна: будь-який збій означає лише «даних немає», і сітка виглядає як
 * до 1.9.1. Курʼєру про це не повідомляємо — йому байдуже, чому ми не знаємо.
 */
async function refreshFreeCells({ manual = false } = {}) {
  if (!state.branchRef) return null;
  // Поштомат, який на цей метод не відповідає (наш Meest, Rozetka — більшість
  // із 900), не почне відповідати ні через пів хвилини, ні до кінця візиту.
  // Питати його вдруге — просто витрачати бюджет викликів.
  if (isTerminalFreeCellsStatus(state.freeCells?.status)) return state.freeCells;
  if (manual) {
    const last = state.freeCellsFetchedAt || 0;
    if (last && Date.now() - last < FREE_CELLS_MANUAL_REFRESH_MS) return state.freeCells;
  }
  state.freeCellsFetchedAt = Date.now();
  try {
    // `silent` — обовʼязково: без нього довідковий запит ходить загальним
    // шляхом api() і на 5xx/502/503 показує курʼєру блокувальний оверлей або
    // тост, яких він не просив. Тут будь-який збій означає лише «даних немає».
    const data = await api(`/locker/free-cells?branchIDRef=${encodeURIComponent(state.branchRef)}`, { noLoader: true, silent: true, timeoutMs: 6000 });
    state.freeCells = normalizeFreeCells(data);
    log('free_cells:' + (state.freeCells.available ? (freeCellsSummary(state.freeCells) || 'ok') : ('none status=' + state.freeCells.status + (state.freeCells.reason ? ' reason=' + state.freeCells.reason : ''))));
    if (isFreeCellsOffline(state.freeCells)) checkPostamatOnlineLive('free_cells_offline');
  } catch (e) {
    state.freeCells = normalizeFreeCells(null);
    log('free_cells:fail ' + (e?.message || e));
  }
  renderSizeGrid();
  updateCellsInfo();
  return state.freeCells;
}

// ─── Звʼязок поштомата з 1С (getPostamatsOnlineStatus) ──────────────────────
//: Екран питає бекенд про звʼязок поштомата у двох випадках:
//:  • після сканування QR — один раз і без fresh (бекенд віддає свій кеш у
//:    межах TTL); якщо поштомат не на звʼязку — плашка в шапці;
//:  • після помилки 25 — опитування з fresh=1 за розкладом (одразу, кожні 10 с
//:    до 1 хв, кожні 20 с до 2 хв, щохвилини до 15 хв), поки поштомат не вийде
//:    на звʼязок або курʼєр не піде від нього. Живий стан — у самій плашці 25
//:    (під шапкою поштомата); червону плашку в шапці тим часом ховаємо, щоб
//:    стан звʼязку не дублювався, і повертаємо, щойно плашку 25 закрили або
//:    опитування зупинили ззовні (нове сканування, вихід від поштомата).
//: Опитування ОДНЕ на екран (createOnlinePoller), усі запити мовчазні: збій
//: означає лише «стан невідомий» — жодних блокерів, оверлеїв і тостів про мережу.
//: Розклад, тексти й розбір відповіді — чисті помічники в postamat-online.js.

/** Номер запуску опитування: ним позначаємо «свою» плашку 25. */
let onlinePollSeq = 0;
/**
 * Покоління стану звʼязку. Росте, коли знання про звʼязок змінилось ззовні:
 * комірка відкрилась, новий поштомат, курʼєр пішов від поштомата. Відповідь
 * на запит, відправлений у попередньому поколінні, застаріла — її не
 * застосовуємо, інакше запізніле «не на звʼязку» повернуло б плашку поверх
 * щойно відкритої комірки.
 */
let onlineEpoch = 0;
/** Перемальовує «N хв тому» на плашці в шапці, поки вона видима (без мережі). */
let onlineBannerClock = null;
const ONLINE_BANNER_CLOCK_MS = 30_000;

const postamatOnline = createOnlinePoller({
  fetchStatus: (ident, opts) => fetchPostamatOnline(ident, opts),
  onUpdate: (update) => onPostamatPollUpdate(update),
  onEnd: (result) => onPostamatPollEnd(result),
});

/** Чим ідентифікуємо поштомат у запиті: ref із QR, інакше branchID. */
function postamatOnlineIdent() {
  return state.branchRef || state.branchID || null;
}

/**
 * Чи питати про звʼязок: не демо (там поштомат вигаданий), є ідентифікатор,
 * мережа не відома як чужа (метод 1С знає лише наші поштомати).
 */
function postamatOnlineApplies() {
  return !state.demoMode && Boolean(postamatOnlineIdent()) && onlineCheckApplies(state.branchNetworkId);
}

/**
 * Один запит до бекенда. `silent` обовʼязковий, як у refreshFreeCells: це
 * довідка, без якої екран працює далі, — її збій не має малювати курʼєру
 * блокер звʼязку чи оверлей «Проблеми на стороні сервісу поштоматів».
 */
async function fetchPostamatOnline(ident, { fresh = false } = {}) {
  const epoch = onlineEpoch;
  const params = new URLSearchParams({ branchIDRef: ident });
  if (fresh) params.set('fresh', '1');
  const data = await api(`/locker/online-status?${params.toString()}`, { noLoader: true, silent: true, timeoutMs: ONLINE_STATUS_TIMEOUT_MS });
  // Поки чекали, комірка відкрилась / курʼєр пішов / новий поштомат — відповідь застаріла.
  if (epoch !== onlineEpoch) {
    log('online:late_drop');
    return null;
  }
  const status = normalizeOnlineStatus(data);
  applyPostamatOnline(status, ident);
  return status;
}

/** Зберегти стан, якщо він досі про ПОТОЧНИЙ поштомат (курʼєр міг відсканувати інший). */
function applyPostamatOnline(status, ident) {
  if (!status || ident !== postamatOnlineIdent()) return false;
  const prev = state.postamatOnline;
  // Збій бекенда без знання (online:null, stale) — як обірваний запит:
  // відомий раніше стан і плашку не чіпаємо.
  if (prev && isBlindStatus(status)) return false;
  state.postamatOnline = status;
  if (!prev || prev.online !== status.online || prev.supported !== status.supported) {
    log(`online:${status.supported ? String(status.online) : 'unsupported'} lastSeen=${status.lastSeen || '-'}${status.stale ? ' stale' : ''}`);
  }
  renderPostamatOnlineBanner();
  return true;
}

function stopOnlineBannerClock() {
  if (onlineBannerClock) {
    clearInterval(onlineBannerClock);
    onlineBannerClock = null;
  }
}

/**
 * Чи на екрані плашка про комірку, яка сама каже про звʼязок: помилка 25 (з
 * живим рядком) або «знову на звʼязку». Позначка — data-conn-line від
 * showCellAlert; плашка, що вже зникає (хрестик), не рахується.
 */
function cellAlertShowsOnline() {
  const box = document.getElementById(CELL_WARN_ALERT_ID);
  return Boolean(box && box.dataset.connLine === '1' && !box.classList.contains('inline-alert-hide'));
}

/**
 * Плашка «Поштомат не на звʼязку · останній сигнал HH:MM, N хв тому» у шапці.
 * Поки під шапкою плашка 25 / «знову на звʼязку» — ховаємо: той самий стан уже
 * в її живому рядку, а дубль лише штовхав сітку розмірів за край екрана.
 * Плашку закрили (хрестик, дія, нова спроба) або її опитування зупинили ззовні
 * (вона вже не стежить за звʼязком) — малюємо за поточним станом.
 */
function renderPostamatOnlineBanner() {
  const el = document.getElementById('postamat_online_banner');
  const text = offlineBannerText(state.postamatOnline, Date.now());
  if (!el || !text || cellAlertShowsOnline()) {
    el?.classList.add('hidden');
    stopOnlineBannerClock();
    return;
  }
  const textEl = el.querySelector('.postamat-online-text') || el;
  textEl.textContent = text;
  el.classList.remove('hidden');
  if (!onlineBannerClock) onlineBannerClock = setInterval(renderPostamatOnlineBanner, ONLINE_BANNER_CLOCK_MS);
}

/**
 * Після сканування QR — окремо і без await, як вільні комірки: екран готовий
 * одразу, а плашка «не на звʼязку» доїде слідом, якщо вона потрібна.
 */
function checkPostamatOnline() {
  if (!postamatOnlineApplies()) return;
  fetchPostamatOnline(postamatOnlineIdent()).catch((e) => log('online:skip ' + (e?.message || e)));
}

/**
 * 1С на запит вільних комірок відповіла «поштомат не на звʼязку»
 * (`postamat_offline`) — це знання про сам поштомат. Один живий запит звʼязку
 * (fresh): знімок після сканування береться з реєстру 1С, який оновлюється
 * раз на 5 хв, і міг ще цього не знати. Опитування за розкладом не
 * запускаємо — воно лише для помилки 25; плашку в шапці малює звичайний шлях.
 */
function checkPostamatOnlineLive(reason) {
  if (!postamatOnlineApplies() || postamatOnline.isActive()) return;
  log('online:check_live ' + reason);
  fetchPostamatOnline(postamatOnlineIdent(), { fresh: true }).catch((e) => log('online:skip ' + (e?.message || e)));
}

/** Новий поштомат: стан звʼязку попереднього недійсний, його опитування — теж. */
function resetPostamatOnline(reason) {
  onlineEpoch += 1;
  postamatOnline.stop(reason);
  state.postamatOnline = null;
  renderPostamatOnlineBanner();
}

/** Курʼєр пішов від поштомата (додому, назад, завершення) — опитування нікому не потрібне. */
function stopPostamatOnline(reason) {
  onlineEpoch += 1;
  postamatOnline.stop(reason);
  stopOnlineBannerClock();
}

/**
 * Комірка відкрилась — отже поштомат на звʼязку, хоч би що казав останній
 * знімок 1С. Опитування зупиняємо, плашку «не на звʼязку» знімаємо.
 */
function notePostamatAnswered() {
  onlineEpoch += 1;
  postamatOnline.stop('opened');
  if (isKnownOffline(state.postamatOnline)) {
    state.postamatOnline = { ...state.postamatOnline, online: true };
    log('online:true by_open');
    renderPostamatOnlineBanner();
  }
}

/**
 * Помилка 25 → опитування звʼязку за розкладом. Плашку 25 позначаємо номером
 * запуску: живий рядок пишемо лише у «свою», а не в плашку, яка встигла її
 * замінити (напр. «комірка могла відкритись»).
 * Причина помилки («немає звʼязку») — у живому рядку, а не в тексті плашки,
 * тому й без опитування (демо, чужа мережа, метод 1С поштомат не знає) рядок
 * є — нерухомий, лише з причиною.
 */
function watchPostamatAfterError25(kind, onRetry) {
  const box = document.getElementById(CELL_WARN_ALERT_ID);
  // Бекенд уже сказав (свіжою відповіддю), що цей поштомат методу не
  // підтримує, — питати марно. «Не підтримується» зі stale — збій, не вирок.
  if (!postamatOnlineApplies() || isFinalUnsupported(state.postamatOnline)) {
    if (box) paintLiveLine(box, liveStatusLine({ phase: 'unavailable' }));
    return false;
  }
  onlinePollSeq += 1;
  const pollId = onlinePollSeq;
  if (box) box.dataset.onlinePoll = String(pollId);
  log('online:poll_start kind=' + kind);
  // Знімок після сканування показуємо одразу, лише якщо він каже «не на
  // звʼязку»: «на звʼязку» помилка 25 щойно спростувала.
  const initial = isKnownOffline(state.postamatOnline) ? state.postamatOnline : null;
  return postamatOnline.start(postamatOnlineIdent(), { pollId, kind, onRetry }, { initial });
}

/** Плашка 25 цього запуску опитування, якщо вона ще на екрані. */
function postamatPollAlert(context) {
  const box = document.getElementById(CELL_WARN_ALERT_ID);
  if (!box || !context || box.dataset.onlinePoll !== String(context.pollId)) return null;
  if (box.classList.contains('inline-alert-hide')) return null;
  return box;
}

/** Живий рядок «немає звʼязку з HH:MM · перевіряємо далі» у плашці 25 цього запуску (null — прибрати). */
function renderPostamatLiveLine(context, line) {
  const box = postamatPollAlert(context);
  if (box) paintLiveLine(box, line);
}

/**
 * Один рядок «точка + текст» перед кнопками плашки; line = null — прибрати.
 * line.cta === 'problem' (15 хв без звʼязку) — виділяємо «Повідомити проблему».
 */
function paintLiveLine(box, line) {
  let el = box.querySelector('.inline-alert-live');
  box.classList.toggle('inline-alert-cta-problem', Boolean(line && line.cta === 'problem'));
  if (!line) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.className = 'inline-alert-live';
    el.setAttribute('role', 'status');
    const dot = document.createElement('span');
    dot.className = 'inline-alert-live-dot';
    dot.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'inline-alert-live-text';
    el.append(dot, text);
    box.insertBefore(el, box.querySelector('.inline-alert-actions'));
  }
  el.dataset.tone = line.tone;
  el.querySelector('.inline-alert-live-text').textContent = line.text;
}

function onPostamatPollUpdate({ status, phase, context }) {
  renderPostamatOnlineBanner();
  renderPostamatLiveLine(context, liveStatusLine({ status, phase }));
}

function onPostamatPollEnd({ reason, status, context, polls }) {
  log(`online:poll_end reason=${reason} polls=${polls}`);
  if (reason === 'online') {
    renderPostamatOnlineBanner();
    announcePostamatBackOnline(context);
  } else if (reason === 'timeout') {
    renderPostamatLiveLine(context, liveStatusLine({ status, phase: 'timeout' }));
  } else if (reason === 'unsupported') {
    // Перевіряти далі нема як — лишаємо саму причину помилки 25.
    renderPostamatLiveLine(context, liveStatusLine({ phase: 'unavailable' }));
  } else if (reason === 'opened') {
    renderPostamatLiveLine(context, null);
  } else if (reason !== 'restart') {
    // Зупинили ззовні (нове сканування, додому, назад, завершення): обіцянка
    // «перевіряємо далі — повідомимо» більше не діє, лишаємо нейтральний рядок.
    // 'restart' не чіпаємо: плашка вже належить новому запуску.
    renderPostamatLiveLine(context, liveStatusLine({ status, phase: 'stopped' }));
    // Звʼязок ця плашка більше не показує — і шапку вже не ховає: інакше
    // застаріла 25 (напр. поштомата A) лишала б без червоної «не на звʼязку»
    // шапку наступного поштомата, доки плашку хтось не закриє.
    const box = postamatPollAlert(context);
    if (box) delete box.dataset.connLine;
    renderPostamatOnlineBanner();
  }
}

/**
 * Поштомат знову на звʼязку. Якщо плашка 25 ще на екрані — замінюємо її на
 * «спробуйте відкрити комірку» з тим самим повтором (той самий id: бронь 1С не
 * скасовує, і комірка ще може відкритись сама — крок лишається критичним).
 * Якщо курʼєр уже взявся за іншу посилку — лише коротке повідомлення.
 */
function announcePostamatBackOnline(context) {
  try { tg?.HapticFeedback?.notificationOccurred?.('success'); } catch (_) {}
  if (!postamatPollAlert(context)) {
    toast('Поштомат знову на звʼязку', LOCKER_ERROR_TOAST_MS);
    return;
  }
  const { kind, onRetry } = context;
  const view = backOnlineAlert(kind);
  showCellAlert({ kind, tone: 'success', title: view.title, message: view.message, onRetry, connLine: true });
}

/**
 * Плашка про комірку: помилка 25, «знову на звʼязку», «комірка могла
 * відкритись». Одна на екран (той самий id — нова замінює попередню).
 * Стоїть ПІД шапкою поштомата і компактна: заголовок, коротка інструкція,
 * живий рядок звʼязку, «Відкрити ще раз» і «Повідомити проблему» в один ряд —
 * так на телефоні видно і шапку, і сітку розмірів, і «Відкрити повторно».
 * connLine — плашка сама каже про звʼязок (25, «знову на звʼязку»): поки вона
 * на екрані, червона плашка в шапці схована (renderPostamatOnlineBanner);
 * курʼєр закрив її чи опитування зупинили ззовні (позначку знімає
 * onPostamatPollEnd) — шапка знову показує стан звʼязку.
 */
function showCellAlert({ kind, tone = 'warn', title, message, onRetry, connLine = false }) {
  createInlineAlert({
    id: CELL_WARN_ALERT_ID,
    host: CELL_ALERT_HOST_ID,
    compact: true,
    tone,
    title,
    message,
    actions: [
      { label: 'Відкрити ще раз', onClick: () => { try { onRetry && onRetry(); } catch (_) {} } },
      { label: 'Повідомити проблему', className: 'inline-alert-btn-problem', onClick: () => { try { openProblemMenuFor(kind); } catch (_) {} } },
    ],
    onClose: () => renderPostamatOnlineBanner(),
  });
  const box = document.getElementById(CELL_WARN_ALERT_ID);
  if (box && connLine) box.dataset.connLine = '1';
  renderPostamatOnlineBanner();
  return box;
}

// ─── Кінець блоку звʼязку поштомата ─────────────────────────────────────────

function resetDeliveryConfirmButton() {
  const btn = document.getElementById('btn_delivery_confirm');
  if (!btn) return;
  btn.classList.add('hidden');
  btn.classList.remove('confirm-ready');
}

function markDeliveryConfirmReady() {
  const btn = document.getElementById('btn_delivery_confirm');
  if (!btn) return;
  btn.classList.remove('hidden');
  btn.classList.add('confirm-ready');
  btn.focus?.();
}

function initSettingsModal() {
  const modal = document.getElementById('settings_modal');
  const openBtn = document.getElementById('btn_settings');
  if (!modal || !openBtn) return;

  const closeButtons = modal.querySelectorAll('[data-settings-close]');
  const panel = modal.querySelector('.settings-panel');
  const themeRadios = modal.querySelectorAll('input[name="settings_theme"]');
  const tsdToggle = modal.querySelector('#settings_tsd');
  const autoToggle = modal.querySelector('#settings_auto_open');
  const trainingToggle = modal.querySelector('#settings_training');

  function applyToForm(settings) {
    const safe = settings || state.settings || {};
    const theme = safe.theme === 'light' ? 'light' : 'dark';
    themeRadios.forEach((radio) => {
      radio.checked = radio.value === theme;
    });
    if (tsdToggle) tsdToggle.checked = !!safe.tsdMode;
    if (autoToggle) autoToggle.checked = !!safe.autoOpenCell;
    if (trainingToggle) trainingToggle.checked = !!safe.trainingMode;
  }

  applyToForm(state.settings);

  window.addEventListener('webapp:settings-applied', (evt) => {
    applyToForm(evt?.detail);
  });

  function openModal() {
    modal.classList.add('visible');
    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');
    const focusTarget = panel?.querySelector('[data-focus-default]') || panel?.querySelector('input,button,select');
    focusTarget?.focus?.();
  }

  function closeModal() {
    modal.classList.remove('visible');
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');
    openBtn?.focus?.();
  }

  openBtn.addEventListener('click', () => openModal());
  closeButtons.forEach((btn) => btn.addEventListener('click', () => closeModal()));
  modal.addEventListener('click', (evt) => {
    if (evt.target === modal) closeModal();
  });
  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape' && modal.classList.contains('visible')) {
      evt.preventDefault();
      closeModal();
    }
  });

  themeRadios.forEach((radio) => {
    radio.addEventListener('change', (evt) => {
      const value = evt.currentTarget?.value === 'light' ? 'light' : 'dark';
      saveCourierSettings({ theme: value }).catch((err) => log('settings:theme_save_fail ' + (err?.message || err)));
    });
  });

  tsdToggle?.addEventListener('change', (evt) => {
    setTsdMode(!!evt.currentTarget.checked, { persistRemote: true, showToast: true });
  });

  autoToggle?.addEventListener('change', (evt) => {
    saveCourierSettings({ autoOpenCell: !!evt.currentTarget.checked }).catch((err) => log('settings:auto_save_fail ' + (err?.message || err)));
  });

  trainingToggle?.addEventListener('change', (evt) => {
    const enabled = !!evt.currentTarget.checked;
    saveCourierSettings({ trainingMode: enabled }).catch((err) => log('settings:training_save_fail ' + (err?.message || err)));
    void handleTrainingToggle(enabled, { source: 'settings' }).catch((err) => log('training:toggle_fail ' + (err?.message || err)));
  });
}
function trainingSettingEnabled() {
  return !!(state.settings && state.settings.trainingMode);
}

function updateDemoButtonLabel() {
  // Demo button removed from UI
}

async function enableDemoMode({ source = 'manual', restartTraining = false } = {}) {
  if (state.demoMode) {
    updateDemoButtonLabel();
    if (trainingSettingEnabled()) {
      simulator.start({ restart: restartTraining });
    } else if (simulator.isActive()) {
      simulator.stop({ userInitiated: false });
    }
    return;
  }

  state.demoMode = true;
  updateDemoButtonLabel();
  log('demo:enabled', { source });
  state.demoInventoryStatuses = {};

  if (trainingSettingEnabled()) {
    simulator.start({ restart: restartTraining });
  } else if (simulator.isActive()) {
    simulator.stop({ userInitiated: false });
  }

  await applyLockerCtx({
    branch: { branchID: 'DEMO-001', branchIDRef: '00000000-0000-0000-0000-DEMOLOCK', fullName: 'Демо Поштомат #1' },
    courier: { courierID: 'DEMO-C', courierIDSQL: 'DEMO-C', name: 'Демо Кур’єр' },
  });
  state.branchID = 'DEMO-001';
  state.branchRef = '00000000-0000-0000-0000-DEMOLOCK';
  // Демо-поштомат «як Епіцентр» (див. демо free-cells в api.js): правило
  // «спершу посилки Епіцентру» в демо теж діє. Демо лишається прохідним —
  // спершу вкладається демо-посилка Епіцентру, далі решта.
  state.branchNetworkId = EPICENTR_NETWORK_ID;
  simulator.notify('locker-resolved', { branchID: state.branchID, branchRef: state.branchRef, source });

  await loadParcels(undefined, true, { noLoader: false });
  try {
    const hasEp = (state.delivery || []).some((p) => isEpAgent(p.agent));
    if (!hasEp) {
      // Агент — рівно значення 1С: з 18.09 збіг точний, і «Епіцентр онлайн демо»
      // вже не вважався б Епіцентром (ні пріоритету, ні правила «спершу Епіцентр»).
      const demoEp = { parcelID: 'DLV-EP1', parcelNumber: 'EP12345', barcode: 'EP12345BAR', agent: EPICENTR_PARCEL_AGENT };
      state.delivery.unshift(demoEp);
      state.deliveryTotal = state.delivery.length;
      state.deliveryIndex = {};
      state.delivery.forEach((p) => {
        const vars = [p.parcelID, p.parcelNumber, p.barcode].filter(Boolean);
        vars.forEach((v) => {
          state.deliveryIndex[String(v).replace(/[^A-Za-z0-9]/g, '').toUpperCase()] = p.parcelID;
        });
      });
      log('demo:inject_epitsentr');
    }
  } catch (_) {}

  renderPickupList();
  renderDeliveryList();
  updateProgress();
  await updateCellsInfo();
  await openInventoryPanel();
}

async function disableDemoMode({ source = 'manual' } = {}) {
  if (!state.demoMode) return;
  state.demoMode = false;
  updateDemoButtonLabel();
  log('demo:disabled', { source });
  simulator.stop({ userInitiated: source === 'manual' });
  location.reload();
}

async function setDemoMode(enabled, { source = 'manual', restartTraining = false } = {}) {
  if (enabled) {
    await enableDemoMode({ source, restartTraining });
  } else {
    await disableDemoMode({ source });
  }
}

async function handleTrainingToggle(enabled, { source = 'unknown', initial = false } = {}) {
  state.settings = state.settings || {};
  state.settings.trainingMode = !!enabled;
  lastTrainingMode = state.settings.trainingMode;
  if (enabled) {
    if (!state.demoMode) {
      await setDemoMode(true, { source: source === 'initial' ? 'training-initial' : source, restartTraining: true });
    } else {
      simulator.start({ restart: initial });
      if (state.branchID) {
        simulator.notify('locker-resolved', { branchID: state.branchID, branchRef: state.branchRef || null, source: 'training-replay' });
      }
    }
  } else if (simulator.isActive()) {
    simulator.stop({ userInitiated: source === 'settings' });
  }
}

const INVENTORY_STATUS_MAP = {
  found: { label: 'Посилка знайдена', completed: true },
  missing: { label: 'Посилка відсутня', completed: true },
  failed: { label: 'Не вдалося провести інвентаризацію', completed: false },
};

function setInventoryButtonsDisabled(disabled, activeKey = null) {
  document.querySelectorAll('.inventory-action-buttons button').forEach((btn) => {
    if (!btn) return;
    const key = btn.getAttribute('data-result');
    if (disabled && activeKey && key === activeKey) {
      btn.classList.add('is-loading');
    } else {
      btn.classList.remove('is-loading');
    }
    btn.disabled = !!disabled;
  });
}

function currentScreenId() {
  const el = document.querySelector('.panel:not(.hidden)');
  return el?.id || 'screen_start';
}

function setInventoryStatus(message, warn = false) {
  const el = document.getElementById('inventory_status');
  if (!el) return;
  if (!message) {
    el.textContent = '';
    el.classList.add('hidden');
    el.classList.remove('warn');
    return;
  }
  el.textContent = String(message);
  el.classList.toggle('warn', !!warn);
  el.classList.remove('hidden');
}

function formatInventoryDate(iso) {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return null;
    return new Intl.DateTimeFormat('uk-UA', { dateStyle: 'short', timeStyle: 'short' }).format(dt);
  } catch (e) {
    log('inventory:date_parse_error ' + (e?.message || e));
    return null;
  }
}

function renderInventoryBranchHeader() {
  const branch = state.inventoryBranch || {};
  const titleEl = document.getElementById('inventory_branch_title');
  const addrEl = document.getElementById('inventory_branch_address');
  const branchParts = [];
  const branchId = branch.branchID ?? state.branchID ?? null;
  if (branchId) branchParts.push(branchId);
  if (branch.branchNumber) branchParts.push(`№${branch.branchNumber}`);
  const ref = branch.branchIDRef ?? state.branchRef;
  if (ref) branchParts.push(ref);
  if (titleEl) titleEl.textContent = branchParts.length ? branchParts.join(' · ') : 'Поштомат';
  if (addrEl) {
    const address = branch.branchAddress ?? '';
    addrEl.textContent = address;
    addrEl.classList.toggle('hidden', !address);
  }
}

function renderInventoryList() {
  const list = document.getElementById('inventory_list');
  if (!list) return;
  list.innerHTML = '';
  const tasks = Array.isArray(state.inventoryTasks) ? state.inventoryTasks : [];
  const pending = tasks.filter((task) => !task.status);
  if (state.inventoryLoading) {
    for (let i = 0; i < 3; i += 1) {
      const sk = document.createElement('div');
      sk.className = 'skeleton-item';
      list.appendChild(sk);
    }
    return;
  }
  if (!tasks.length || !pending.length) {
    setInventoryStatus('Усі завдання виконані 🎉');
    return;
  }
  const selectedKey = state.inventorySelected ? inventoryTaskKey(state.inventorySelected) : null;
  const pendingCount = pending.length;
  const msg = pendingCount === 1
    ? 'Залишилась 1 посилка без статусу. Оберіть її зі списку та зафіксуйте статус.'
    : `Залишилось ${pendingCount} посилок без статусу. Оберіть посилку зі списку та зафіксуйте статус.`;
  setInventoryStatus(msg);
  pending.forEach((task) => {
    const key = inventoryTaskKey(task);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'inventory-task-btn';
    if (selectedKey && selectedKey === key) btn.classList.add('active');
    const title = document.createElement('strong');
    title.textContent = task.parcelNumber || task.parcelId;
    const status = document.createElement('span');
    status.className = 'inventory-status';
    status.textContent = task.status ? String(task.status) : 'Без статусу';
    btn.appendChild(title);
    btn.appendChild(status);
    const metaParts = [];
    if (task.operationId) metaParts.push(`ID: ${task.operationId}`);
    const started = formatInventoryDate(task.startedAt);
    if (started) metaParts.push(`Почато ${started}`);
    if (metaParts.length) {
      const meta = document.createElement('span');
      meta.className = 'inventory-meta-small';
      meta.textContent = metaParts.join(' · ');
      btn.appendChild(meta);
    }
    btn.addEventListener('click', () => selectInventoryTask(task));
    list.appendChild(btn);
  });
}

function renderInventoryDetails() {
  const wrap = document.getElementById('inventory_details');
  const parcelEl = document.getElementById('inventory_task_parcel');
  const statusEl = document.getElementById('inventory_task_status');
  const startedEl = document.getElementById('inventory_task_started');
  const notesEl = document.getElementById('inventory_notes');
  if (!wrap || !notesEl) return;
  const task = state.inventorySelected;
  if (!task) {
    wrap.classList.add('hidden');
    notesEl.value = '';
    if (parcelEl) parcelEl.textContent = '';
    if (statusEl) statusEl.textContent = '';
    if (startedEl) {
      startedEl.textContent = '';
      startedEl.classList.add('hidden');
    }
    return;
  }
  wrap.classList.remove('hidden');
  if (parcelEl) parcelEl.textContent = task.parcelNumber || task.parcelId;
  if (statusEl) statusEl.textContent = '';
  if (startedEl) {
    const started = formatInventoryDate(task.startedAt);
    startedEl.textContent = started ? `Почато: ${started}` : '';
    startedEl.classList.toggle('hidden', !started);
  }
  notesEl.value = task.notes ? String(task.notes) : '';
}

function selectInventoryTask(task) {
  state.inventorySelected = task;
  renderInventoryList();
  renderInventoryDetails();
  setInventoryStatus('Оберіть статус для посилки та додайте коментар за потреби');
}

function clearInventorySelection() {
  state.inventorySelected = null;
  renderInventoryList();
  renderInventoryDetails();
}

async function loadInventoryTasks({ silent = false } = {}) {
  if (!state.branchID) {
    setInventoryStatus('Спочатку відскануйте поштомат', true);
    return;
  }
  state.inventoryLoading = true;
  if (!silent) setInventoryStatus('Завантаження…');
  renderInventoryList();
  try {
    if (state.demoMode) {
      const demoRaw = [
        {
          parcelId: 'DEMO-INV-001',
          parcelNumber: 'INV-DEMO-001',
          operationId: 'DEMO-INV-001',
          startedAt: new Date(Date.now() - 5 * 60_000).toISOString(),
        },
        {
          parcelId: 'DEMO-INV-002',
          parcelNumber: 'INV-DEMO-002',
          operationId: 'DEMO-INV-002',
          startedAt: new Date(Date.now() - 12 * 60_000).toISOString(),
        },
        {
          parcelId: 'DEMO-INV-003',
          parcelNumber: 'INV-DEMO-003',
          operationId: 'DEMO-INV-003',
          startedAt: new Date(Date.now() - 25 * 60_000).toISOString(),
        },
      ];
      state.inventoryBranch = {
        branchID: state.branchID,
        branchIDRef: state.branchRef,
        branchNumber: 'DEMO',
        branchAddress: 'м. Київ, вул. Навчальна, 1',
      };
      const completedMap = state.demoInventoryStatuses || {};
      state.inventoryTasks = demoRaw
        .map((item) => normalizeInventoryTask(item))
        .filter((item) => item);
      state.inventoryTasks.forEach((task) => {
        const key = inventoryTaskKey(task);
        if (completedMap[key]) {
          task.status = completedMap[key];
          task.resultText = completedMap[key];
        }
      });
      state.inventoryPendingCount = state.inventoryTasks.filter((item) => !item.status).length;
      state.inventorySelected = null;
      renderInventoryBranchHeader();
      if (!state.inventoryPendingCount) {
        setInventoryStatus('Усі завдання виконані 🎉');
      } else {
        setInventoryStatus('Оберіть посилку зі списку та зафіксуйте статус');
      }
      return;
    }
    const data = await api(`/inventory/tasks?branchID=${encodeURIComponent(state.branchID)}`, {
      loaderText: silent ? undefined : 'Інвентаризація…',
      noLoader: silent,
    });
    state.inventoryBranch = {
      branchID: data?.branchID ?? state.branchID ?? null,
      branchIDRef: data?.branchIDRef ?? state.branchRef ?? null,
      branchNumber: data?.branchNumber ?? null,
      branchAddress: data?.branchAddress ?? data?.address ?? null,
    };
    const tasksRaw = Array.isArray(data?.tasks) ? data.tasks : [];
    state.inventoryTasks = tasksRaw
      .map((item) => normalizeInventoryTask(item))
      .filter((item) => item);
    state.inventoryPendingCount = state.inventoryTasks.filter((item) => !item.status).length;
    state.inventorySelected = null;
    renderInventoryBranchHeader();
    if (!state.inventoryTasks.length) {
      setInventoryStatus('Завдань для інвентаризації немає');
    }
  } catch (e) {
    log('inventory:load_error ' + (e?.message || e));
    if (!silent) toast('Не вдалося завантажити інвентаризацію');
    state.inventoryTasks = [];
    state.inventoryPendingCount = 0;
    if (!silent) setInventoryStatus('Не вдалося завантажити дані', true);
  } finally {
    state.inventoryLoading = false;
    renderInventoryList();
    renderInventoryDetails();
  }
  return state.inventoryTasks;
}

async function openInventoryPanel({ reload = true, silent = false } = {}) {
  if (!state.branchID) {
    toast('Спочатку відскануйте поштомат');
    return;
  }
  state.lastScreenBeforeInventory = currentScreenId();
  renderInventoryBranchHeader();
  show('screen_inventory');
  simulator.notify('inventory-opened', { demo: !!state.demoMode });
  if (reload) {
    setInventoryStatus('Завантаження…');
    await loadInventoryTasks({ silent });
    return;
  }
  renderInventoryList();
  renderInventoryDetails();
  const pending = state.inventoryPendingCount || 0;
  if (!state.inventoryTasks.length) {
    setInventoryStatus('Завдань для інвентаризації немає');
  } else if (!pending) {
    setInventoryStatus('Усі завдання виконані 🎉');
  } else {
    setInventoryStatus(`Залишилось ${pending} посилок без статусу`);
  }
}

function closeInventoryPanel() {
  const target = state.lastScreenBeforeInventory || (state.branchID ? 'screen_pickup' : 'screen_start');
  show(target);
  clearInventorySelection();
  setInventoryStatus('');
}

async function submitInventoryStatus(resultKey) {
  if (inventorySubmitting) return;
  if (!state.inventorySelected) {
    toast('Оберіть посилку зі списку');
    return;
  }
  const config = INVENTORY_STATUS_MAP[resultKey];
  if (!config) return;
  const notesEl = document.getElementById('inventory_notes');
  if (!notesEl) return;
  let payload;
  try {
    payload = buildInventoryPayload(state.inventorySelected, {
      resultText: config.label,
      completed: config.completed,
      notes: notesEl.value,
    });
  } catch (e) {
    toast(e?.message || 'Перевірте коментар');
    notesEl.focus();
    return;
  }
  inventorySubmitting = true;
  setInventoryButtonsDisabled(true, resultKey);
  try {
    if (state.demoMode) {
      const key = inventoryTaskKey(state.inventorySelected);
      state.demoInventoryStatuses = state.demoInventoryStatuses || {};
      state.demoInventoryStatuses[key] = config.label;
      log('inventory:demo_status', { key, result: config.label });
      simulator.notify('inventory-status', { status: config.label, demo: true });
      toast('Статус збережено');
      state.inventorySelected = null;
      await loadInventoryTasks({ silent: true });
      const remainingDemo = state.inventoryPendingCount || 0;
      if (remainingDemo > 0) {
        setInventoryStatus(`Залишилось ${remainingDemo} посилок без статусу`);
      } else {
        setInventoryStatus('Усі завдання виконані 🎉');
        simulator.notify('inventory-cleared', { demo: true });
        closeInventoryPanel();
      }
      return;
    }
    await api(`/inventory/result?branchID=${encodeURIComponent(state.branchID)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      loaderText: 'Збереження…',
    });
    log('inventory:status_sent', { parcelId: state.inventorySelected.parcelId, result: resultKey });
    simulator.notify('inventory-status', { status: config.label, demo: false });
    toast('Статус збережено');
    await loadInventoryTasks({ silent: true });
    const remaining = state.inventoryPendingCount || 0;
    if (remaining > 0) {
      setInventoryStatus(`Залишилось ${remaining} посилок без статусу`);
    } else {
      setInventoryStatus('Усі завдання виконані 🎉');
      simulator.notify('inventory-cleared', { demo: false });
      closeInventoryPanel();
    }
  } catch (e) {
    log('inventory:status_error ' + (e?.message || e));
    toast('Не вдалося зберегти статус');
  } finally {
    inventorySubmitting = false;
    setInventoryButtonsDisabled(false);
  }
}

const AGENT_LOGO_MAP = {
  'Епіцентр онлайн_поштомати': 'Епіцентр.png',
  'МАУДАУ ТОВ': 'МАУДАУ ТОВ.png',
  'Міст ЕКСПРЕС': 'Міст ЕКСПРЕС.png',
  'CAINIAO': 'CAINIAO.png',
  'iHerb': 'iHerb.png',
  'LPP': 'LPP.png',
  'NEXT': 'NEXT.png',
  'TEMU': 'TEMU.png',
  'UpakovkaPoint24': 'UpakovkaPoint24.png',
};

function agentLogoSources(agent) {
  const fallback = `/logos/nologo.png?v=${encodeURIComponent(APP_VERSION)}`;
  // Always provide at least the fallback
  if (!agent) return [fallback];
  const normSpaces = (s) => s.replace(/[\u00A0\u2000-\u200A\u202F\u205F\u3000]/g, ' ');
  const collapseSpaces = (s) => s.replace(/\s+/g, ' ').trim();
  const tryNormalize = (s) => {
    try { return collapseSpaces(normSpaces(s.normalize ? s.normalize('NFC') : s)); } catch { return collapseSpaces(normSpaces(s)); }
  };
  const safeRaw = tryNormalize(String(agent));
  // Heuristic mapping: if назва містить 'Епіцентр' → примусово мапимо на відомий файл
  let mapped = AGENT_LOGO_MAP[safeRaw];
  const lower = safeRaw.toLowerCase();
  if (!mapped && lower.includes('епіцентр')) {
    mapped = 'Епіцентр.png';
  }
  const srcs = [];
  if (mapped) srcs.push(`/logos/${mapped}?v=${encodeURIComponent(APP_VERSION)}`);
  // Base variants
  srcs.push(`/logos/${encodeURIComponent(safeRaw)}.png?v=${encodeURIComponent(APP_VERSION)}`);
  srcs.push(`/logos/${safeRaw}.png?v=${encodeURIComponent(APP_VERSION)}`);
  // Transformations: spaces→underscore, spaces→hyphen, remove-spaces
  const underscored = safeRaw.replace(/\s+/g, '_');
  const hyphened = safeRaw.replace(/\s+/g, '-');
  const nospace = safeRaw.replace(/\s+/g, '');
  if (underscored !== safeRaw) srcs.push(`/logos/${underscored}.png?v=${encodeURIComponent(APP_VERSION)}`);
  if (hyphened !== safeRaw) srcs.push(`/logos/${hyphened}.png?v=${encodeURIComponent(APP_VERSION)}`);
  if (nospace !== safeRaw) srcs.push(`/logos/${nospace}.png?v=${encodeURIComponent(APP_VERSION)}`);
  // Fallback at the end
  if (!srcs.includes(fallback)) srcs.push(fallback);
  return srcs;
}

const _missingLogoNotified = new Set();
const _MISSING_LOGO_MAX = 200;

// Shared helper: creates a parcel button DOM element (used by both pickup and delivery lists)
function createParcelButton(p, { isGhost, isProblem, problemType, onClick, logoPrefix }) {
  const num = p.parcelNumber || p.miniSticker || p.barcode || '???';
  const barcode = p.barcode && p.barcode !== num ? p.barcode : (p.miniSticker && p.miniSticker !== num ? p.miniSticker : null);
  const btn = document.createElement('button');
  btn.className = 'parcel-btn';
  if (isGhost) btn.classList.add('ghost');
  if (isProblem) {
    btn.classList.add('problem');
    if (problemType) {
      btn.classList.add('problem-' + problemType);
      btn.title = problemTypeHuman(problemType);
    }
  }
  const srcs = agentLogoSources(p.agent);
  if (srcs) {
    const img = document.createElement('img');
    img.className = 'agent-logo';
    img.alt = '';
    let idx = 0;
    img.style.display = 'none';
    img.onload = () => { img.style.display = ''; };
    img.src = srcs[idx];
    img.onerror = () => {
      idx += 1;
      if (idx < srcs.length) {
        img.src = srcs[idx];
      } else {
        img.style.display = 'none';
        const key = logoPrefix + '|' + (p.agent || '');
        if (!_missingLogoNotified.has(key)) {
          if (_missingLogoNotified.size >= _MISSING_LOGO_MAX) _missingLogoNotified.clear();
          _missingLogoNotified.add(key);
          log('logo-missing', p.agent);
        }
      }
    };
    btn.appendChild(img);
  }
  const content = document.createElement('div');
  content.className = 'parcel-content';
  const numEl = document.createElement('span');
  numEl.textContent = num;
  content.appendChild(numEl);
  if (barcode) {
    const bc = document.createElement('span');
    bc.className = 'barcode';
    bc.textContent = barcode;
    content.appendChild(bc);
  }
  btn.appendChild(content);
  btn.onclick = onClick;
  return btn;
}

function renderSkeletonItems(wrap, count = 4) {
  for (let i = 0; i < count; i++) {
    const sk = document.createElement('div');
    sk.className = 'skeleton-item';
    wrap.appendChild(sk);
  }
}

const DEFAULT_PROBLEM_GROUPS = Object.freeze({
  locker: [
    {
      key: 'locker',
      title: 'Проблеми поштомату',
      description: null,
      requiresParcel: false,
      items: [
        { code: 'locker_not_working', title: 'Поштомат не працює', sortOrder: 10 },
        { code: 'locker_no_internet', title: 'Немає інтернету', sortOrder: 20 },
        { code: 'locker_no_mobile', title: 'Немає мобільного звʼязку', sortOrder: 30 },
        { code: 'locker_no_power', title: 'Немає живлення/ДБЖ', sortOrder: 40 },
      ],
    },
  ],
  pickup: [
    {
      key: 'pickup',
      title: 'Проблеми забору',
      description: null,
      requiresParcel: true,
      items: [
        { code: 'cell_not_open', title: 'Комірка не відкривається', sortOrder: 10 },
        { code: 'parcel_absent', title: 'Посилка відсутня', sortOrder: 20 },
      ],
    },
  ],
  delivery: [
    {
      key: 'delivery',
      title: 'Проблеми вкладення',
      description: null,
      requiresParcel: true,
      items: [
        { code: 'no_free_cells', title: 'Немає вільних комірок', sortOrder: 10 },
        { code: 'no_required_size', title: 'Немає потрібного розміру', sortOrder: 20 },
        { code: 'cell_not_open', title: 'Комірка не відкривається', sortOrder: 30 },
        // Назва — як у довіднику типів: на неї посилається порада після 13 «змінити розмір».
        { code: 'epicenter_swap_failed', title: EPICENTR_SWAP_PROBLEM_TITLE, sortOrder: 40 },
      ],
    },
  ],
});

const FALLBACK_PROBLEM_TITLE_MAP = (() => {
  const map = {};
  Object.values(DEFAULT_PROBLEM_GROUPS).forEach((groupList) => {
    groupList.forEach((group) => {
      group.items.forEach((item) => {
        map[item.code] = item.title;
      });
    });
  });
  return map;
})();

function ensureProblemTemplateState() {
  if (!state.problemTemplates) {
    state.problemTemplates = { locker: [], pickup: [], delivery: [] };
  }
  return state.problemTemplates;
}

function fallbackProblemGroups(flow) {
  const groups = DEFAULT_PROBLEM_GROUPS[flow] || [];
  return groups.map((group) => ({
    key: group.key,
    title: group.title,
    description: group.description,
    requiresParcel: !!group.requiresParcel,
    items: group.items.map((item) => ({
      code: item.code,
      title: item.title,
      sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : 100,
    })),
  }));
}

function normalizeProblemGroups(flow, payload) {
  let rawGroups = [];
  if (payload && Array.isArray(payload.groups)) {
    rawGroups = payload.groups;
  } else if (Array.isArray(payload)) {
    rawGroups = payload;
  }
  const normalized = rawGroups
    .map((group, idx) => {
      if (!group || typeof group !== 'object') return null;
      const items = Array.isArray(group.items) ? group.items : [];
      const normalizedItems = items
        .map((item, index) => {
          if (!item || typeof item !== 'object') return null;
          const code = String(item.code || '').trim();
          if (!code) return null;
          const title = (item.title && String(item.title).trim()) || FALLBACK_PROBLEM_TITLE_MAP[code] || code;
          const sortOrder = typeof item.sortOrder === 'number' ? item.sortOrder : (index + 1) * 10;
          return { code, title, sortOrder };
        })
        .filter(Boolean);
      if (!normalizedItems.length) return null;
      return {
        key: String(group.key || `group_${idx}`),
        title: (group.title && String(group.title).trim()) || '',
        description: group.description ? String(group.description).trim() : null,
        requiresParcel: !!group.requiresParcel,
        items: normalizedItems,
      };
    })
    .filter(Boolean);
  if (!normalized.length) {
    return fallbackProblemGroups(flow);
  }
  return normalized;
}

function requiresProblemNote(template) {
  if (!template) return false;
  const code = String(template.code || '').toLowerCase();
  if (!code) return false;
  if (code === 'other' || code.endsWith('_other')) return true;
  const title = String(template.title || '').toLowerCase();
  return title.includes('інш');
}

function applyProblemTemplates(flow, payload) {
  const templatesState = ensureProblemTemplateState();
  templatesState[flow] = normalizeProblemGroups(flow, payload);
  renderProblemOptions(flow);
}

function renderProblemOptions(flow) {
  let containerId = null;
  if (flow === 'pickup') containerId = 'pickup_problem_options';
  if (flow === 'delivery') containerId = 'delivery_problem_options';
  if (!containerId) return;
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';
  const groups = (ensureProblemTemplateState()[flow]) || [];
  if (!groups.length) {
    const empty = document.createElement('div');
    empty.className = 'problem-empty';
    empty.textContent = 'Немає доступних опцій';
    container.appendChild(empty);
    return;
  }
  groups.forEach((group, idx) => {
    const multipleGroups = groups.length > 1;
    const showHeader = multipleGroups || (group.title && group.title.length > 0);
    if (showHeader) {
      const heading = document.createElement('div');
      heading.className = 'problem-group-title';
      heading.textContent = group.title || 'Проблеми';
      container.appendChild(heading);
      if (group.description) {
        const desc = document.createElement('div');
        desc.className = 'problem-group-description';
        desc.textContent = group.description;
        container.appendChild(desc);
      }
    }
    group.items.forEach((item) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'small problem-btn';
      btn.dataset.problemCode = item.code;
      if (requiresProblemNote(item)) {
        btn.dataset.requiresNote = 'true';
      }
      btn.textContent = item.title || item.code;
      container.appendChild(btn);
    });
    if (idx < groups.length - 1) {
      const separator = document.createElement('div');
      separator.className = 'problem-group-separator';
      container.appendChild(separator);
    }
  });
}

function findProblemTemplateTitle(code) {
  if (!code) return '';
  const normalizedCode = String(code).trim();
  const templatesState = ensureProblemTemplateState();
  for (const flow of Object.keys(templatesState)) {
    const groups = templatesState[flow] || [];
    for (const group of groups) {
      const match = group.items.find((item) => item.code === normalizedCode);
      if (match) return match.title;
    }
  }
  return FALLBACK_PROBLEM_TITLE_MAP[normalizedCode] || normalizedCode;
}

async function initProblemTemplates() {
  applyProblemTemplates('locker', []);
  applyProblemTemplates('pickup', []);
  applyProblemTemplates('delivery', []);
  const flows = ['locker', 'pickup', 'delivery'];
  await Promise.all(
    flows.map(async (flow) => {
      try {
        const data = await api(`/problem-templates?flow=${flow}`, { noLoader: true });
        applyProblemTemplates(flow, data);
        const total = (Array.isArray(data?.groups) ? data.groups : []).reduce((acc, group) => acc + (Array.isArray(group?.items) ? group.items.length : 0), 0);
        log(`problem_templates:loaded flow=${flow} count=${total}`);
      } catch (err) {
        log(`problem_templates:load_fail flow=${flow} err=${err?.message || err}`);
      }
    }),
  );
}

function problemTypeHuman(t) {
  return findProblemTemplateTitle(t);
}

function renderPickupList() {
  const wrap = document.getElementById('pickup_list');
  if (!wrap) return;
  wrap.innerHTML = '';
  if (state.loading) {
    for (let i = 0; i < 4; i++) {
      const sk = document.createElement('div');
      sk.className = 'skeleton-item';
      wrap.appendChild(sk);
    }
    return;
  }
  if (!state.pickup.length) {
    wrap.innerHTML = '<em>Немає посилок на забір</em>';
    return;
  }
  state.pickup.forEach((p) => {
    wrap.appendChild(createParcelButton(p, {
      isGhost: state.processedPickup.has(p.parcelID),
      isProblem: state.skippedPickup?.has(p.parcelID),
      problemType: state.pickupProblemTypes?.[p.parcelID],
      onClick: () => selectPickup(p),
      logoPrefix: 'pickup',
    }));
  });
}

function selectPickup(p) {
  if (state.processedPickup.has(p.parcelID)) return;
  clearCellWarn();
  const normalizedNumber = p.parcelNumber || p.miniSticker || p.barcode || p.parcelID || null;
  const normalizedBarcode = p.barcode && p.barcode !== normalizedNumber ? p.barcode : (p.miniSticker && p.miniSticker !== normalizedNumber ? p.miniSticker : null);
  state.currentPickup = {
    ...p,
    parcelNumber: normalizedNumber,
    barcode: normalizedBarcode || null,
    miniSticker: p.miniSticker || null,
  };
  // Switch to details view
  document.getElementById('pickup_list')?.classList.add('hidden');
  document.getElementById('pickup_details')?.classList.remove('hidden');
  // Після відправленої проблеми sendProblem ховає весь блок дій — і без цього
  // рядка наступна посилка лишалась без «Ввести стікер», «Підтвердити» і
  // «Відкрити повторно» до кінця сесії (симетрично до #delivery_actions).
  document.getElementById('pickup_actions')?.classList.remove('hidden');
  // Hide navigation to delivery while in details
  document.getElementById('pickup_to_delivery_wrap')?.classList.add('hidden');
  // Ensure manual/progress panels are reset for this selection
  resetPickupScanPanel();
  // Fill header meta
  const code = state.currentPickup.parcelNumber || '???';
  document.getElementById('pickup_parcel_num')?.replaceChildren(document.createTextNode(code));
  const bc = state.currentPickup.barcode && state.currentPickup.barcode !== code ? state.currentPickup.barcode : '';
  const bcEl = document.getElementById('pickup_parcel_barcode');
  if (bcEl) bcEl.textContent = bc;
  hidePickupProblem();
  // Enable resend hidden by default
  document.getElementById('btn_pickup_resend')?.classList.add('hidden');
  // Auto-open cell immediately
  pickupOpen();
}

// ─── «Спершу посилки Епіцентру» (рішення власника 20.10) ────────────────────
// Лише на поштоматах мережі Епіцентру (state.branchNetworkId з /locker/by-qr):
// поки є невкладені посилки Епіцентру без «Проблеми», комірка для іншої посилки
// не відкривається. Саме правило — у листковому locker-ep-first.js (спільне з
// courier.html); тут лише стан візиту і те, що бачить курʼєр.

const EP_FIRST_ALERT_ID = 'alert_ep_first';
// Опрацьовані за візит переживають перезавантаження сторінки (див.
// locker-ep-first.js): свій ключ localStorage у цього екрана.
const EP_FIRST_STORAGE_KEY = epFirstStorageKey(LOCKER_CLIENT_APP_MIN1);

/** Чиї записи і за яку добу: інший курʼєр на тому ж телефоні чи вчора — не наші. */
function epFirstOwner() {
  let courier = '';
  try {
    const q = new URLSearchParams(window.location.search);
    courier = q.get('courier_meest_id') || q.get('tg_user_id') || '';
  } catch (_) {}
  return { day: kyivDay(), courier };
}

/**
 * Сховище опрацьованих за візит. Перше звернення за життя сторінки підхоплює
 * записане до перезавантаження («Спробувати знову», Back до маршруту й назад).
 */
function epFirstVisits() {
  if (!state.epFirstVisits) {
    state.epFirstVisits = restoreEpFirstVisits(browserStorage(), EP_FIRST_STORAGE_KEY, epFirstOwner());
  }
  return state.epFirstVisits;
}

/** Після кожної зміни візиту. Демо не пишемо: наступне демо має знову показати правило. */
function saveEpFirstVisits() {
  if (state.demoMode) return;
  persistEpFirstVisits(browserStorage(), EP_FIRST_STORAGE_KEY, state.epFirstVisits, epFirstOwner());
}

/**
 * Що сервер знає про цей візит (/locker/parcels: insertedParcelIds,
 * problemParcelIds) — у памʼять візиту. Лише додає опрацьовані.
 */
function applyEpFirstFromServer(data) {
  if (state.demoMode || !data) return;
  if (noteServerHandledInVisit(epFirstVisits(), state.branchRef, data)) {
    log('ep_first:server_handled');
    saveEpFirstVisits();
  }
}

/** null — комірку для посилки p відкривати можна; інакше { count }. */
function epFirstBlock(p) {
  return epicentrBlockFor(p, state.delivery, handledInVisit(epFirstVisits(), state.branchRef), state.branchNetworkId);
}

/**
 * Пояснення, чому комірка не відкривається: стійка плашка, а не тост —
 * курʼєру треба прочитати, що робити далі. Місце — загальне #inline_alert_host
 * (під шапкою живуть лише плашки про саму комірку, див. showCellAlert), тому
 * прокручуємо до неї: тап міг бути внизу довгого списку.
 */
function showEpFirstAlert(block) {
  const text = epFirstText(block.count);
  createInlineAlert({
    id: EP_FIRST_ALERT_ID,
    compact: true,
    tone: 'warn',
    title: text.title,
    message: text.message,
  });
  try { document.getElementById(EP_FIRST_ALERT_ID)?.scrollIntoView?.({ block: 'nearest' }); } catch (_) {}
  log('ep_first:block left=' + block.count);
}

/**
 * Після зміни списку: блок знято — плашку прибираємо; ще діє — оновлюємо
 * лічильник у заголовку, щоб курʼєр не бачив застарілу кількість.
 */
function syncEpFirstAlert(view) {
  const box = document.getElementById(EP_FIRST_ALERT_ID);
  if (!box) return;
  if (!view || !view.active) {
    box.remove();
    return;
  }
  const title = box.querySelector('.inline-alert-title');
  if (title) title.textContent = epFirstText(view.count).title;
}

function renderDeliveryList() {
  const wrap = document.getElementById('delivery_list');
  if (!wrap) return;
  wrap.innerHTML = '';
  if (state.loading) {
    for (let i = 0; i < 4; i++) {
      const sk = document.createElement('div');
      sk.className = 'skeleton-item';
      wrap.appendChild(sk);
    }
    return;
  }
  const ep = epFirstListView(state.delivery, handledInVisit(epFirstVisits(), state.branchRef), state.branchNetworkId);
  syncEpFirstAlert(ep);
  if (!state.delivery.length) {
    wrap.innerHTML = '<em>Немає посилок на вкладення</em>';
    return;
  }
  if (ep.active) {
    const hint = document.createElement('div');
    hint.className = 'ep-first-hint';
    hint.setAttribute('role', 'status');
    hint.textContent = epFirstText(ep.count).hint;
    wrap.appendChild(hint);
  }
  state.delivery.forEach((p) => {
    const btn = createParcelButton(p, {
      isGhost: false,
      isProblem: state.skippedDelivery?.has(p.parcelID),
      problemType: state.deliveryProblemTypes?.[p.parcelID],
      onClick: () => selectDelivery(p),
      logoPrefix: 'delivery',
    });
    // Посилки Епіцентру, що чекають, — підсвічені; решта — приглушені, але
    // натискаються: тап покаже, чому комірка поки не відкривається.
    if (ep.active) btn.classList.add(ep.pendingIds.has(p.parcelID) ? 'ep-first' : 'ep-wait');
    wrap.appendChild(btn);
  });
}

function selectDelivery(p) {
  // Блок «спершу Епіцентр» — ДО будь-яких змін стану: поточна посилка, сітка
  // й попередження про комірку лишаються як були.
  const epBlock = epFirstBlock(p);
  if (epBlock) {
    showEpFirstAlert(epBlock);
    // Скан ховає список (deliveryScan) — повертаємо його, якщо посилки в
    // роботі немає; інакше екран не чіпаємо.
    if (!state.currentDelivery) showDeliveryMenus();
    renderDeliveryList();
    return;
  }
  clearCellWarn();
  const normalizedNumber = p.parcelNumber || p.miniSticker || p.barcode || p.parcelID || null;
  const normalizedBarcode = p.barcode && p.barcode !== normalizedNumber ? p.barcode : (p.miniSticker && p.miniSticker !== normalizedNumber ? p.miniSticker : null);
  state.currentDelivery = {
    parcelID: p.parcelID,
    parcelNumber: normalizedNumber,
    agent: p.agent || null,
    barcode: normalizedBarcode || null,
    miniSticker: p.miniSticker || null,
  };
  state.currentDeliveryAttempts = 0;
  // Ховаємо кнопку повернення у режимі роботи з конкретною посилкою
  document.getElementById('btn_delivery_back')?.classList.add('hidden');
  // Update header meta
  const numEl = document.getElementById('delivery_parcel_num');
  const bcEl = document.getElementById('delivery_parcel_barcode');
  const primaryLabel = state.currentDelivery.parcelNumber || '???';
  if (numEl) numEl.textContent = primaryLabel;
  if (bcEl) bcEl.textContent = state.currentDelivery.barcode && state.currentDelivery.barcode !== primaryLabel ? state.currentDelivery.barcode : '';
  // Start with default size S and immediately try to open a cell (same behavior as scanning)
  state.currentCellSize = DEFAULT_CELL_SIZE;
  state.currentDeliveryTrans = null;
  // Відмови 13 «змінити розмір» — про попередню посилку (або попередній вибір цієї).
  state.cellResizeRejected = null;
  document.getElementById('delivery_actions')?.classList.add('hidden');
  // Сітка розмірів — однакова для ВСІХ посилок, включно з Епіцентром.
  // До 1.9.1 епіцентрівська посилка ховала сітку і відкривала найменшу комірку
  // без вибору курʼєра; половина проблем у цій темі виросла саме звідти.
  // Налаштування автовідкриття самого курʼєра поважаємо як раніше — на проді
  // воно увімкнене у 9 осіб із 576.
  hideDeliveryMenus();
  resetDeliveryConfirmButton();
  highlightSizeButton(null);
  document.getElementById('delivery_cell_label')?.classList.add('hidden');
  state.currentDeliveryBooked = isEpicentrParcel(state.currentDelivery);
  const grid = renderSizeGrid();

  if (state.currentDeliveryBooked) {
    // Посилка Епіцентру має заброньовану комірку — відкриваємо одразу, без
    // перевірки вільних (див. EPICENTR_BOOKED_OPEN_SIZE). Сітка лишається:
    // курʼєр може обрати інший розмір, якщо посилка не влізла.
    state.currentCellSize = EPICENTR_BOOKED_OPEN_SIZE;
    highlightSizeButton(state.currentCellSize);
    log('delivery:epicentr_booked_open size=' + state.currentCellSize + ' none_free=' + Boolean(grid.noneFree));
    runWithLoader(() => attemptDeliveryOpen(), 'Відкриваємо заброньовану комірку…', { immediate: true });
    return;
  }

  if (grid.noneFree) {
    // Кажемо ДО спроби, а не після трьох відмов від 1С. Дані щойно з поштомата,
    // і жодне відкриття тут не спрацює — відкривати нема чого.
    document.getElementById('delivery_actions')?.classList.remove('hidden');
    showNoFreeCellsAlert();
    return;
  }

  // Автовідкриття не починає з розміру, про який поштомат щойно сказав
  // «вільних немає»: це гарантована відмова 1С і зайва спроба для курʼєра.
  // `null` означає, що вгору від S вільного немає взагалі (типова картина:
  // лишився тільки XS). Вниз ми не спускаємось ніколи — габарити посилки
  // бачить курʼєр, — тому рішення віддаємо йому, як і решті 98% курʼєрів.
  const autoSize = firstOpenableSize(grid, state.currentCellSize);

  if (!autoOpenEnabled() || autoSize === null) {
    document.getElementById('delivery_actions')?.classList.remove('hidden');
    focusFirstAvailableSize(grid);
    toast('Оберіть розмір комірки для відкривання');
    return;
  }

  state.currentCellSize = autoSize;
  highlightSizeButton(state.currentCellSize);
  // Show a loader immediately so user sees feedback from the click
  runWithLoader(() => attemptDeliveryOpen(), 'Підбір комірки…', { immediate: true });
}

/**
 * Фокус на розмірі, з якого курʼєру найімовірніше почати.
 *
 * Якщо вгору від поточного нічого немає — фокусуємо перший доступний у всій
 * сітці (хай навіть менший). Фокус на кнопці з підписом «немає» — це для
 * ТЗД-клавіатури Enter у нікуди.
 */
function focusFirstAvailableSize(grid) {
  const size = firstOpenableSize(grid, state.currentCellSize || DEFAULT_CELL_SIZE)
    || anyOpenableSize(grid)
    || state.currentCellSize || DEFAULT_CELL_SIZE;
  document.querySelector(`#size_selector .size-btn[data-size="${size}"]`)?.focus?.();
}

/**
 * Вільних комірок немає ЖОДНОГО розміру. Стійка плашка, а не тост: курʼєру
 * треба не «спробувати ще раз», а перейти до «Проблеми», і кнопка для цього
 * має бути під рукою.
 */
function showNoFreeCellsAlert() {
  const at = freeCellsActualLabel(state.freeCells);
  createInlineAlert({
    id: 'alert_no_free_cells',
    // Саме 'warn': у app.css є .inline-alert-warn, а 'warning' дав би клас,
    // якого в стилях немає, і найважливіша плашка екрана вийшла б сірою.
    tone: 'warn',
    title: 'У поштоматі немає вільних комірок',
    // Кнопки при цьому лишаються натисними (див. renderSizeGrid), тому текст
    // не каже «відкривати нема чого»: курʼєр бачить поштомат, а ми — ні.
    message: at
      ? `Станом на ${at} вільних комірок жодного розміру немає. Якщо бачите вільну — спробуйте, інакше повідомте проблему.`
      : 'Вільних комірок жодного розміру немає. Якщо бачите вільну — спробуйте, інакше повідомте проблему.',
    actions: [{ label: 'Повідомити проблему', onClick: () => showDeliveryProblemMenu() }],
  });
  log('delivery:no_free_cells sizes=' + (freeCellsSummary(state.freeCells) || '—'));
}

// Delivery problem reporting
function showDeliveryProblemMenu() {
  resetDeliveryProblemNote();
  document.getElementById('delivery_problem_menu')?.classList.remove('hidden');
}
function hideDeliveryProblemMenu() {
  document.getElementById('delivery_problem_menu')?.classList.add('hidden');
  resetDeliveryProblemNote();
}
function resetDeliveryProblemNote() {
  state.pendingDeliveryProblem = null;
  const wrap = document.getElementById('delivery_problem_note_wrap');
  wrap?.classList.add('hidden');
  const inp = document.getElementById('delivery_problem_note');
  if (inp) inp.value = '';
}
function promptDeliveryProblemNote(code, requiresNote) {
  state.pendingDeliveryProblem = { code, requiresNote: !!requiresNote };
  const wrap = document.getElementById('delivery_problem_note_wrap');
  wrap?.classList.remove('hidden');
  const inp = document.getElementById('delivery_problem_note');
  if (inp) {
    inp.value = '';
    inp.focus();
  }
}
async function submitDeliveryProblem(ptype, note) {
  if (!state.currentDelivery) return;
  const transId = state.currentDeliveryTrans || null;
  state.pendingDeliveryProblem = null;
  try {
    const problemResp = await api('/locker/problem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        parcelID: state.currentDelivery.parcelID || null,
        parcelNumber: state.currentDelivery.parcelNumber || null,
        parcelBarcode: state.currentDelivery.barcode || null,
        problemType: ptype,
        note: note || null,
        branchIDRef: state.branchRef || null,
        branchFreeCells: typeof state.postamatInfo?.freeCells === 'number' ? state.postamatInfo.freeCells : null,
        parcelAgent: parcelAgentForRequest(state.currentDelivery),
        transID: transId,
      }),
      loaderText: 'Надсилання…'
    });
    const pid = state.currentDelivery.parcelID;
    // Поштомат запамʼятовуємо зараз: «Undo» може прийти вже після нового скану.
    const problemBranchRef = state.branchRef;
    state.skippedDelivery = state.skippedDelivery || new Set();
    state.skippedDelivery.add(pid);
    state.deliveryProblemTypes = state.deliveryProblemTypes || {};
    state.deliveryProblemTypes[pid] = ptype;
    // «Проблема» будь-якого типу — вихід із блоку «спершу Епіцентр» для цієї посилки.
    noteHandledInVisit(epFirstVisits(), problemBranchRef, pid, 'problem');
    saveEpFirstVisits();
    state.currentDeliveryAttempts = 0;
    log('problem:mark ' + pid + ' type=' + ptype);
    // Стек для undo
    state._deliveryProblemStack = state._deliveryProblemStack || [];
    state._deliveryProblemStack.push({ parcelID: pid });
    hideDeliveryProblemMenu();
    // Повертаємось до списку без видалення елемента
    state.currentDelivery = null;
    state.currentDeliveryTrans = null;
    showDeliveryMenus();
    renderDeliveryList();
    updateProgress();
    showUndoToast('Проблему збережено', () => {
      // Undo handler
      state.skippedDelivery.delete(pid);
      delete state.deliveryProblemTypes[pid];
      // Проблему відкликано — посилка Епіцентру знову чекає вкладення.
      forgetProblemInVisit(epFirstVisits(), problemBranchRef, pid);
      saveEpFirstVisits();
      log('problem:undo ' + pid);
      renderDeliveryList();
      updateProgress();
    });
    // Проблему збережено, але 1С не зняла бронь комірки (27): курʼєр має знати,
    // що комірка лишилась заброньованою. Тост зайнятий «Undo» і зникає за 6 с —
    // тому стійка плашка, як у alert_late_confirm.
    const cancelWarning = lockerCancelWarning(problemResp);
    if (cancelWarning) {
      createInlineAlert({ id: 'alert_cancel_not_released', tone: 'warn', message: cancelWarning });
      log('problem:cancel_not_released ' + pid + ' code=' + (problemResp?.lockerError?.code || ''));
    }
  } catch (e) {
    toast('Помилка');
  }
}

function hideDeliveryMenus() {
  document.getElementById('delivery_list')?.classList.add('hidden');
  // top controls (delivery_controls) більше не використовуються
  document.getElementById('btn_delivery_scan')?.classList.add('hidden');
  document.getElementById('delivery_controls_top')?.classList.add('hidden');
}
function showDeliveryMenus() {
  document.getElementById('delivery_list')?.classList.remove('hidden');
  document.getElementById('delivery_actions')?.classList.add('hidden');
  // Посилки в роботі вже немає («Проблема», підтвердження, новий скан) — порада
  // «оберіть інший розмір» над списком ні до чого, а її кнопка вела б у меню
  // проблем без посилки. Плашку 25 не чіпаємо: вона про комірку, яка ще може
  // відкритись.
  clearCellResizeWarn();
  // Clear header meta when returning to list
  clearDeliveryMeta();
  resetDeliveryConfirmButton();
  highlightSizeButton(null);
  document.getElementById('delivery_cell_label')?.classList.add('hidden');
  // Повертаємо кнопку назад у списку
  document.getElementById('btn_delivery_back')?.classList.remove('hidden');
  document.getElementById('btn_delivery_scan')?.classList.remove('hidden');
  document.getElementById('delivery_controls_top')?.classList.remove('hidden');
}

function clearDeliveryMeta() {
  const numEl = document.getElementById('delivery_parcel_num');
  const bcEl = document.getElementById('delivery_parcel_barcode');
  if (numEl) numEl.textContent = '';
  if (bcEl) bcEl.textContent = '';
}

function resetPickupScanPanel() {
  document.getElementById('pickup_scan_panel')?.classList.add('hidden');
  document.getElementById('pickup_manual_panel')?.classList.add('hidden');
}

// Mark hardware interaction active while performing a critical locker operation
async function withOperation(fn) {
  setOperationActive(true);
  try {
    return await fn();
  } finally {
    setOperationActive(false);
  }
}

function findCaseInsensitive(source, key) {
  // NOTE: Kept for potential future use, but extractLockerError now relies on backend's lockerError field.
  if (!source || typeof source !== 'object') return undefined;
  const target = String(key || '').toLowerCase();
  if (!target) return undefined;
  for (const currentKey of Object.keys(source)) {
    if (currentKey.toLowerCase() === target) return source[currentKey];
  }
  return undefined;
}

// ─── Чисті хелпери помилок поштомата (тести: tests/locker-errors.test.js) ────
// Зразок — humanizeDeliverError з екрана адресної доставки (delivery3.js).
// Правило те саме: зрозумілий український текст від 1С показуємо ЯК Є, нічого
// не «покращуємо». Переписуємо лише технічне сміття — стек-трейси 1С, порожні
// описи, англомовні внутрішні коди. Для кодів, які реально трапляються у
// курʼєрів, свій текст: він каже, ЩО СТАЛОСЬ і ЩО РОБИТИ.
// Нижче — лише чисті функції: без DOM, без мережі, без state.

/** Скільки тримати текст помилки на екрані (мс): на 1.6 с його не прочитати. */
const LOCKER_ERROR_TOAST_MS = 6000;

/** Фолбек, коли код невідомий, а опис — технічне сміття або порожній. */
const LOCKER_FALLBACK_ERROR =
  'Поштомат не виконав операцію. Спробуйте ще раз, а якщо повториться — натисніть «Проблема»';

/**
 * Коди, які реально трапляються у курʼєрів (частоти — з логів проду).
 * Опис від 1С для них або технічний, або надто короткий: «Немає вільних
 * комірок!» не каже, що робити далі, а код 13 приносить стек-трейс модуля.
 *
 * Той самий код прилітає і на вкладенні, і на заборі, тому текст залежить від
 * операції: курʼєру, який ВИЙМАЄ посилку, не можна казати «класти вдруге не
 * треба». Ключі — від точного до загального: `insert_open`/`insert_confirm`/
 * `remove_open`/`remove_confirm` → `insert`/`remove` → `default`.
 */
const LOCKER_ERROR_TEXTS = {
  // 20 — найчастіша: 20 689 показів у 328 курʼєрів за 14 днів.
  // Розміри перебираються автоматично, тож порада залежить від того, чи
  // лишився наступний розмір: інакше тост радить «спробуйте більший» рівно
  // тоді, коли в сітці поруч усі більші розміри вже погашені написом «немає».
  '20': {
    insert_open_bigger: 'У поштоматі немає вільної комірки цього розміру — пробуємо більший',
    insert: 'У поштоматі немає вільної комірки для цієї посилки. Натисніть «Проблема» → «Немає вільних комірок»',
    default: 'У поштоматі немає вільної комірки. Натисніть «Проблема» → «Немає вільних комірок»',
  },
  // 24 — операція фактично вже пройшла, повторювати її не можна. Списку і
  // кнопки повернення на цьому кроці на екрані немає, тому «поверніться до
  // списку» курʼєр виконати не може — ведемо у «Проблему».
  '24': {
    insert: 'Вкладення цієї посилки вже підтверджено раніше — класти її вдруге не треба. Посилка лишиться у списку: натисніть «Проблема» і передайте її номер у підтримку',
    remove: 'Забір цієї посилки вже підтверджено раніше — виймати її вдруге не треба. Натисніть «Проблема» і передайте номер посилки у підтримку',
    default: 'Цю операцію з посилкою вже підтверджено раніше — повторювати її не треба. Натисніть «Проблема» і передайте номер посилки у підтримку',
  },
  // 9 — поштомат не бачить відправлення або ідентифікатор операції застарів.
  '9': {
    insert: 'Поштомат не знайшов цю посилку у списку на вкладення. Відкрийте комірку ще раз, а якщо повториться — натисніть «Проблема»',
    remove: 'Поштомат не знайшов цю посилку у себе. Відкрийте комірку ще раз, а якщо повториться — натисніть «Проблема»',
    default: 'Поштомат не знайшов цю посилку. Відкрийте комірку ще раз, а якщо повториться — натисніть «Проблема»',
  },
  // 25 — поштомат не підтвердив відкриття: зник звʼязок (1С з 16.10). Інший розмір
  // не допоможе, тому перебору немає; бронь 1С не скасовує — комірка може
  // відкритись пізніше, і курʼєр має знати, що з нею робити.
  // На відкритті це ТІЛО компактної плашки під шапкою (warnCellNotOpened): що
  // сталось — у її заголовку «Поштомат не відкрив комірку», причина й стан
  // звʼязку — у живому рядку, дії — кнопками. Тому лише інструкція, 1–2 рядки.
  '25': {
    insert_open: 'Нічого не кладіть. Якщо комірка відкриється пізніше — закрийте її порожньою.',
    remove_open: 'Спробуйте ще раз за хвилину або натисніть «Повідомити проблему».',
    default: 'Поштомат не відкрив комірку — немає звʼязку з поштоматом. Спробуйте ще раз за хвилину або натисніть «Проблема»',
  },
  // 13 — внутрішня помилка 1С; у деталях прилітає стек-трейс модуля. З 21.10 тим
  // самим кодом 1С на ВІДКРИТТІ для вкладення відмовляє змінити розмір броні
  // Епіцентру («Не вдалося змінити розмір комірки Epicentr. HTTP 400») — тоді
  // варіант `resize` (ознака — isCellResizeRejected). Розміри на 13 автоматично
  // не перебираються (рішення 26.10), тому на відкритті «відкрийте комірку ще раз»
  // не пишемо. insert_open_resize — загальний варіант поради (для журналу): саму
  // плашку warnCellResizeRejected будує під посилку, з відхиленими розмірами.
  // На підтвердженні 13 — відхилений transID: «Підтвердити» ще раз дасть те саме,
  // допомагає лише нове відкриття — там текст, що був до 26.10.
  // На інших операціях розміру не обрати — там загальний текст.
  // Тексти спільні з courier.html (locker-cells.js).
  '13': {
    insert_open_resize: CELL_RESIZE_REJECTED_HINT,
    insert_confirm: LOCKER_TEXT_1C_FAILURE_CONFIRM,
    remove_confirm: LOCKER_TEXT_1C_FAILURE_CONFIRM,
    default: LOCKER_TEXT_1C_FAILURE,
  },
  // 400 — підтвердження прийшло після того, як поштомат закрив операцію.
  '400': {
    insert: 'Час на підтвердження вийшов, поштомат закрив операцію. Відкрийте комірку ще раз і підтвердіть одразу після вкладення',
    remove: 'Час на підтвердження вийшов, поштомат закрив операцію. Відкрийте комірку ще раз і підтвердіть одразу після того, як заберете посилку',
    default: 'Час на підтвердження вийшов, поштомат закрив операцію. Відкрийте комірку ще раз і підтвердіть одразу після операції',
  },
  // 22 «Pocessing not found» і 500 «Internal server error» — англомовні
  // технічні відповіді, які курʼєр бачив сирими: за 14 днів на проді 132 покази
  // у 41 курʼєра і 123 у 36 відповідно. Обидві означають збій на боці сервісу
  // поштоматів, а не помилку курʼєра, тому порада однакова: повторити, а далі
  // скаржитись. Код 3 «PostStation not found» свідомо НЕ мапимо: 11 показів в
  // одного курʼєра за 14 днів, і фолбек із кодом для підтримки тут доречніший.
  '22': 'Сервіс поштоматів тимчасово не обробляє цей запит. Спробуйте ще раз за хвилину, а якщо повториться — натисніть «Проблема»',
  '500': 'Збій на боці сервісу поштоматів. Спробуйте ще раз за хвилину, а якщо повториться — натисніть «Проблема»',
  // 27 — 1С (з 20.10) не змогла скасувати бронь на боці Епіцентру. Приходить лише
  // на скасуванні броні, тобто після «Проблеми» на вкладенні: проблему вже
  // зареєстровано, а комірка лишилась заброньованою — сервер повторить скасування
  // сам. Курʼєру лишається одне: не класти в неї посилку і зачинити її.
  '27': 'Поштомат не зняв бронь комірки. Нічого в неї не кладіть і закрийте її.',
  // Відповідь, яку ми не розібрали взагалі: стан комірки невідомий. На
  // підтвердженні «нічого не кладіть» уже пізно — посилка в комірці.
  unknown_response: {
    insert_open: 'Поштомат відповів незрозуміло — невідомо, чи відкрилась комірка. Не кладіть у неї посилку, закрийте її і відкрийте ще раз',
    insert_confirm: 'Поштомат відповів незрозуміло — невідомо, чи зарахувалось вкладення. Посилку з комірки не забирайте, натисніть «Підтвердити» ще раз, а якщо повториться — «Проблема»',
    remove_open: 'Поштомат відповів незрозуміло — невідомо, чи відкрилась комірка. Нічого з неї не беріть, закрийте її і відкрийте ще раз',
    remove_confirm: 'Поштомат відповів незрозуміло — невідомо, чи зарахувався забір. Натисніть «Підтвердити» ще раз, а якщо повториться — «Проблема»',
    default: 'Поштомат відповів незрозуміло — невідомо, чи відкрилась комірка. Нічого з нею не робіть, закрийте її і відкрийте ще раз',
  },
};

/**
 * Заглушки, якими бекенд замінює порожній опис від 1С
 * (`_enrich_response` підставляє «…повернув помилку без опису.»). Для курʼєра
 * це не опис: ні що сталось, ні що робити, ні коду для підтримки. Перевірку
 * «текст українською» вони проходять, тому відсіюємо їх окремо — інакше
 * фолбек із кодом не спрацює ЖОДНОГО разу на реальній відповіді бекенда.
 */
const LOCKER_PLACEHOLDER_DETAILS = [
  'Сервіс поштоматів повернув помилку без опису.',
  'Сервіс поштоматів повернув помилку.',
  'Сервіс поштоматів повернув несподівану відповідь.',
  'Невідома відповідь від сервісу поштоматів.',
];

/** Операція → ключ тексту. Порожній ключ = операція невідома, беремо default. */
function lockerFlowKey(context) {
  switch (context) {
    case 'locker_insert': return 'insert_open';
    case 'locker_insert_confirm': return 'insert_confirm';
    case 'locker_remove': return 'remove_open';
    case 'locker_remove_confirm': return 'remove_confirm';
    // Скасування броні після «Проблеми» на вкладенні (courierNotConfirmedInsert).
    case 'locker_insert_cancel': return 'insert_cancel';
    default: return '';
  }
}

/**
 * Текст коду під конкретну операцію: варіант → точний ключ → флоу → default.
 * variant — різновид того самого коду за описом 1С (`resize` для 13).
 */
function pickLockerText(entry, flowKey, hasBiggerSize, variant) {
  if (typeof entry === 'string') return entry;
  if (!entry || typeof entry !== 'object') return null;
  const keys = [];
  if (variant) {
    if (flowKey) keys.push(`${flowKey}_${variant}`);
    keys.push(variant);
  }
  if (flowKey && hasBiggerSize) keys.push(`${flowKey}_bigger`);
  if (flowKey) keys.push(flowKey, flowKey.split('_')[0]);
  keys.push('default');
  for (const key of keys) {
    const text = Object.prototype.hasOwnProperty.call(entry, key) ? entry[key] : null;
    if (typeof text === 'string' && text) return text;
  }
  return null;
}

/** Прибирає HTML-розмітку, URL і зайві пробіли з тексту помилки. */
function cleanLockerNoise(raw) {
  if (raw == null) return '';
  return String(raw)
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Стек-трейс 1С курʼєру показувати не можна:
 * «{ОбщийМодуль.МодульАПИ.Модуль(7125)}: Значення не є значенням об'єктного
 * типу (reservationID)» — 265 показів за 14 днів. Кирилиця в ньому є, тому
 * перевірка «текст українською» його не відсіює — ловимо окремо.
 */
function looksLikeLockerStackTrace(text) {
  if (!text) return false;
  return (
    /\{[^{}]{0,200}\(\d+(?:[,.]\d+)?\)\s*\}/.test(text) || // {Модуль.Імʼя(7125)}
    /значення не є значенням|значение не является значением/i.test(text) ||
    /\btraceback\b|\bexception\b|\bat [A-Za-z_$][\w$]*\s*\(/i.test(text)
  );
}

/**
 * Текст помилки поштомата для курʼєра.
 * Порядок: відомий код → наш текст «що сталось + що робити» під поточну
 * операцію; інакше зрозумілий український опис від 1С → ЯК Є; решта
 * (стек-трейси, заглушки бекенда, порожнє, англомовне) → фолбек із кодом, щоб
 * підтримка могла зіставити скрін із логом.
 *
 * options: { context — операція (див. lockerFlowKey), hasBiggerSize — чи
 * лишився для цієї посилки більший розмір комірки }.
 */
function humanizeLockerError(code, details, options) {
  const opts = options && typeof options === 'object' ? options : {};
  const codeKey = code == null ? '' : String(code).trim();
  // hasOwnProperty, а не просто [codeKey]: код приходить із відповіді 1С, і
  // значення на кшталт «toString» дало б курʼєру текст функції замість помилки.
  const entry = Object.prototype.hasOwnProperty.call(LOCKER_ERROR_TEXTS, codeKey)
    ? LOCKER_ERROR_TEXTS[codeKey]
    : null;
  // 13 «змінити розмір» (Епіцентр) — свій текст: інший розмір вручну, а не повтор.
  const variant = isCellResizeRejected(codeKey, details) ? 'resize' : '';
  const mapped = pickLockerText(entry, lockerFlowKey(opts.context), Boolean(opts.hasBiggerSize), variant);
  if (mapped) return mapped;

  const raw = details == null ? '' : String(details);
  const text = cleanLockerNoise(raw);
  const looksLikeHtmlPage = /<html|<!doctype|cloudflare|ray id/i.test(raw);
  const isPlaceholder = LOCKER_PLACEHOLDER_DETAILS.some(
    (stub) => stub.toLowerCase() === text.toLowerCase(),
  );
  const isHuman =
    Boolean(text) &&
    !isPlaceholder &&
    !looksLikeHtmlPage &&
    !looksLikeLockerStackTrace(text) &&
    /[А-Яа-яЇїІіЄєҐґ]/.test(text);
  if (isHuman) {
    return text.length > 300 ? `${text.slice(0, 299).trimEnd()}…` : text;
  }
  return codeKey ? `${LOCKER_FALLBACK_ERROR} (код ${codeKey})` : LOCKER_FALLBACK_ERROR;
}

/**
 * Значення transID, які насправді означають «транзакції немає».
 * Один перелік на три місця: тут, `locker_reservations._INVALID_TRANS_IDS`
 * (створення резервації) і `LockerService._INVALID_TRANS_IDS` (перевірка на
 * підтвердженні). Розбіжність між ними означає, що фронт і бекенд по-різному
 * відповідають на питання «чи є транзакція»: екран веде курʼєра до зеленої
 * «Підтвердити», а резервації на сервері немає — або навпаки.
 */
const INVALID_TRANS_IDS = ['', '-', '0', '00', 'none', 'null', 'undefined'];

/**
 * Ідентифікатор транзакції з відповіді 1С на відкриття комірки.
 * Дзеркалить бекенд (locker_reservations.extract_trans_id): «0» — це НЕ
 * транзакція, підтвердження з таким значенням 1С відкидає помилкою 13.
 */
function extractTransId(resp) {
  const root = resp && typeof resp === 'object' ? resp : {};
  const inner = root.result && typeof root.result === 'object' ? root.result : {};
  const open = inner.Open_reserv && typeof inner.Open_reserv === 'object' ? inner.Open_reserv : {};
  for (const candidate of [inner.transID, open.transID, root.transID, inner.reservationID]) {
    const tid = candidate == null ? '' : String(candidate).trim();
    if (tid && !INVALID_TRANS_IDS.includes(tid.toLowerCase())) return tid;
  }
  return null;
}

/**
 * Виняток із ділової помилки поштомата: її текст курʼєр уже побачив.
 * Прапорець потрібен, щоб зверху не лягав другий тост із порадою «спробуйте ще
 * раз» — він перекриває справжню причину (тост один на екран) і радить повтор
 * там, де повтор не спрацює ніколи.
 */
function lockerErrorException(lockerErr) {
  const e = new Error(lockerErr?.message || lockerErr?.details || `Locker error ${lockerErr?.code}`);
  e.lockerErrorShown = true;
  e.lockerCode = lockerErr?.code || null;
  // Сирий опис 1С: за ним відрізняємо різновиди одного коду (13 «змінити розмір»).
  e.lockerDetails = lockerErr?.details || null;
  return e;
}

/** Чи показувати загальний тост про невдале відкриття поверх уже показаної причини. */
function shouldShowGenericOpenError(e) {
  return !(e && e.lockerErrorShown);
}

/**
 * Підпис лічильника комірок. Він знає лише загальну кількість вільних на
 * момент сканування QR — без розбивки за розмірами і без оновлення до виходу
 * з поштомата. Старе «Комірки 5/40» курʼєр читав як відповідь на питання
 * «чи покладу я саме цю посилку», хоча воно на нього не відповідає.
 */
const FREE_CELLS_HINT = 'Усі розміри разом, дані на момент сканування поштомату';

function freeCellsLabel(info) {
  const free = info && typeof info.freeCells === 'number' ? info.freeCells : null;
  const total = info && typeof info.totalCells === 'number' ? info.totalCells : null;
  if (free !== null) return { text: `Вільних усього: ${free}`, title: FREE_CELLS_HINT };
  if (total !== null) return { text: `Комірок усього: ${total}`, title: FREE_CELLS_HINT };
  return { text: 'Вільних усього: ?', title: FREE_CELLS_HINT };
}

function normalizeLockerErrorCode(raw) {
  if (raw === null || raw === undefined) return null;
  const str = String(raw).trim();
  if (!str) return null;
  const lowered = str.toLowerCase();
  if (['0', '00', '000', '200', '201', 'ok', 'success'].includes(lowered)) return null;
  return str;
}

// Simplified: backend enriches all responses with a lockerError field via _enrich_response().
// No need to deep-parse error/errorcode from raw Meest payload on the frontend.
function extractLockerError(payload) {
  if (!payload || typeof payload !== 'object') {
    return {
      code: 'unknown_response',
      details: 'Сервіс поштоматів повернув несподівану відповідь.',
    };
  }

  const hinted = payload.lockerError;
  if (hinted && typeof hinted === 'object') {
    const code = normalizeLockerErrorCode(hinted.code);
    if (code) {
      return {
        code,
        details: hinted.details ? String(hinted.details).trim() : null,
      };
    }
  }

  return null;
}

/**
 * Відмови скасування броні, про які курʼєр має дізнатись після «Проблеми».
 * Бекенд кладе lockerError у відповідь /locker/problem, коли 1С не скасувала
 * бронь (проблему при цьому зареєстровано). Попереджаємо лише там, де бронь
 * справді лишилась і курʼєр може щось зробити: 27 (Епіцентр не зняв її,
 * сервер повторить). 22 «процесингу вже немає» і 400 «посилка не делегована»
 * курʼєру нічого не дають — їх бачить підтримка в «Помилках API поштомату».
 */
const LOCKER_CANCEL_WARNING_CODES = ['27'];

/** Текст попередження про незняту бронь або null, якщо казати нічого. */
function lockerCancelWarning(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const err = extractLockerError(payload);
  if (!err || !LOCKER_CANCEL_WARNING_CODES.includes(err.code)) return null;
  return humanizeLockerError(err.code, err.details, { context: 'locker_insert_cancel' });
}

// ─── Кінець чистих хелперів помилок поштомата ───────────────────────────────

/**
 * Чи лишився для поточної посилки більший розмір комірки.
 * Рахуємо тим самим правилом, що й авто-ескалація в attemptDeliveryOpen, — щоб
 * текст помилки і сітка не суперечили одне одному: тост не має радити «пробуємо
 * більший», коли всі більші розміри вже погашені нулем.
 */
function hasBiggerCellSize() {
  const size = state.currentCellSize;
  if (!size) return false;
  const next = nextSizeForAgent(state.currentDelivery?.agent || null, size, state.freeCells);
  return Boolean(next) && next !== size;
}

function showLockerError(payload, context) {
  const err = extractLockerError(payload);
  if (!err) return null;
  const code = err.code || '';
  const details = err.details && err.details.trim() ? err.details.trim() : '';
  // Курʼєру — людський текст; сирий опис від 1С лишається в логу для підтримки.
  const message = humanizeLockerError(code, details, {
    context,
    hasBiggerSize: hasBiggerCellSize(),
  });
  // 25 на відкритті — стійке попередження (warnCellNotOpened) замість тосту:
  // інструкцію «що робити з коміркою» за секунди тосту не прочитати. Так само 13
  // «змінити розмір» на вкладенні (warnCellResizeRejected): порада потрібна, поки
  // курʼєр обирає інший розмір.
  const sticky = (String(code) === LOCKER_CODE_NOT_OPENED && (context === 'locker_insert' || context === 'locker_remove'))
    || (context === 'locker_insert' && isCellResizeRejected(code, details));
  if (!sticky) toast(message, LOCKER_ERROR_TOAST_MS);
  log(`locker_error ${context || 'op'} code=${code || 'невідомо'} details=${details} shown=${message}`);
  return { code: code || 'невідомо', details, message };
}

function notifyLockerNotFound(customMessage) {
  const primary = customMessage || 'Поштомат не знайдено.';
  const secondary = customMessage ? '' : 'Поштомат зачинено або пошкоджено QR-код.';
  const combined = customMessage ? primary : `${primary}\n${secondary}\nОберіть поштомат вручну або зверніться до підтримки.`;


  if (tg?.showAlert) {
    tg.showAlert(combined);
  } else {
    createInlineAlert({
      title: primary,
      message: secondary,
      tone: 'warn',
      actions: [
        {
          label: 'Вибрати в списку',
          onClick: () => {
            try {
              document.getElementById('btn_scan_start')?.focus();
            } catch (_) {
              /* ignore */
            }
          },
        },
      ],
    });
  }

  toast(primary);
}

// Ensure courier context is present (courierID/courierIDSQL) even if QR resolver didn’t include it
async function ensureCourierContext() {
  if (state.courier && state.courier.courierIDSQL) return;
  // Try cached value from sessionStorage first
  try {
    const cached = sessionStorage.getItem('courierContext');
    if (cached) {
      const ctx = JSON.parse(cached);
      if (ctx && ctx.courierIDSQL) {
        state.courier = ctx;
        return;
      }
    }
  } catch (_) { /* ignore parse errors */ }
  try {
    const ctx = await api('/courier/context', { loaderText: 'Отримання даних кур\u2019єра\u2026' });
    if (ctx && ctx.courierID && ctx.courierIDSQL) {
      state.courier = { courierID: ctx.courierID, courierIDSQL: ctx.courierIDSQL, name: ctx.name };
      try { sessionStorage.setItem('courierContext', JSON.stringify(state.courier)); } catch (_) {}
    }
  } catch (e) {
    log('courier ctx err ' + (e?.message || e));
    throw e;
  }
}
// An open call that failed with a timeout / 5xx may have STILL physically
// opened the cell (the locker hardware acts before the response reaches us).
// A clean business error ("Немає вільних комірок", "Відправлення не знайдено")
// means no cell opened. Distinguish them so we only warn "don't use the cell"
// when the cell could actually be open. 503 is maintenance — excluded.
function isCellMayBeOpenError(e) {
  // Код надійніший за текст: humanizeLockerError тепер переписує повідомлення,
  // і класифікація за рядком розпадеться від першої ж зміни формулювання.
  // unknown_response — відповідь, яку ми не розібрали взагалі: стан комірки
  // так само невідомий, як і після таймауту.
  if (e && e.lockerCode === 'unknown_response') return true;
  const m = (e && e.message) || '';
  return /HTTP\s*50[024]|timeout|перевищив час/i.test(m);
}

/**
 * Відкриває наявне меню «Проблема» для потрібного флоу.
 * Потрібен вихід із попередження про можливо відкриту комірку: якщо повторне
 * відкриття не допомагає, курʼєру нема куди подітись — раніше він лишався
 * наодинці з коміркою і тиснув «відкрити» десятки разів.
 */
function openProblemMenuFor(kind) {
  if (kind === 'delivery') {
    document.getElementById('delivery_actions')?.classList.remove('hidden');
    showDeliveryProblemMenu();
    try { document.getElementById('delivery_problem_menu')?.scrollIntoView({ block: 'center' }); } catch (_) {}
    return;
  }
  document.getElementById('pickup_details')?.classList.remove('hidden');
  showPickupProblem();
  try { document.getElementById('pickup_problem_menu')?.scrollIntoView({ block: 'center' }); } catch (_) {}
}

// Persistent (not transient) safety warning: the cell may be physically open
// but we have no confirmation/transID, so the courier must NOT use it — close
// it and re-open to get a fresh transaction.
// Та сама компактна плашка під шапкою, що й 25 (showCellAlert), з виходом у
// «Повідомити проблему». Про звʼязок вона не каже — червона плашка в шапці
// лишається.
function warnCellMayBeOpen(kind, onRetry) {
  try { hideUpstreamErrorOverlay(); } catch (_) {}
  const isDelivery = kind === 'delivery';
  showCellAlert({
    kind,
    tone: 'warn',
    title: isDelivery ? 'Не вдалось отримати дані для вкладення' : 'Немає підтвердження відкриття комірки',
    message: isDelivery
      ? 'Поштомат не підтвердив відкриття комірки. Якщо вона відкрилась — НЕ кладіть у неї посилку, закрийте її. Потім натисніть «Відкрити ще раз».'
      : 'Поштомат не підтвердив відкриття комірки. Якщо вона відкрилась — закрийте її. Потім натисніть «Відкрити ще раз».',
    onRetry,
  });
}

/**
 * Прибрати попередження про комірку (25 / «не підтвердив відкриття»): воно
 * стосується КОНКРЕТНОЇ спроби. Без цього плашка «нічого не кладіть, закрийте
 * порожньою» висіла поруч із наступним успішним відкриттям і на інших посилках.
 */
function clearCellWarn() {
  try { document.getElementById(CELL_WARN_ALERT_ID)?.remove(); } catch (_) {}
  // Порада «оберіть інший розмір» теж про конкретну спробу.
  clearCellResizeWarn();
  // Плашки 25 більше немає — червона «не на звʼязку» в шапці знову за станом.
  try { renderPostamatOnlineBanner(); } catch (_) {}
}

/** Прибрати лише пораду 13 «змінити розмір» (вихід до списку, «Назад»). */
function clearCellResizeWarn() {
  try { document.getElementById(CELL_RESIZE_ALERT_ID)?.remove(); } catch (_) {}
}

/**
 * Помилка 25: поштомат не відкрив комірку (немає звʼязку) — стійке попередження
 * під шапкою поштомата: заголовок, коротка інструкція (message — текст
 * LOCKER_ERROR_TEXTS['25'] для цієї операції) і живий рядок звʼязку з причиною:
 * одразу питаємо 1С (fresh) і далі за розкладом до 15 хв; щойно поштомат на
 * звʼязку — плашка міняється на «знову на звʼязку» (див.
 * watchPostamatAfterError25). Червона плашка в шапці тим часом схована.
 * Повертає номер запуску опитування або false (перевіряти нема як).
 */
function warnCellNotOpened(kind, message, onRetry) {
  try { hideUpstreamErrorOverlay(); } catch (_) {}
  showCellAlert({ kind, tone: 'warn', title: 'Поштомат не відкрив комірку', message, onRetry, connLine: true });
  return watchPostamatAfterError25(kind, onRetry);
}

/**
 * Помилка 13 «Не вдалося змінити розмір комірки Epicentr» (з 21.10): на посилку
 * вже є бронь комірки іншого розміру. Стійка плашка під шапкою, як 25, але без
 * «Відкрити ще раз»: той самий розмір дасть ту саму відмову, а інший курʼєр
 * обирає в сітці. Порада — під цю посилку (cellResizeRejectedHint): називає
 * відхилені розміри, XS радить, лише коли його не відхилено і він не погашений у
 * сітці, і веде в «Проблему» з типом «Не міняється комірка (Епіцентр)».
 * Комірка НЕ відкривалась, тож крок не критичний — свій id (CELL_RESIZE_ALERT_ID).
 */
function warnCellResizeRejected() {
  const rejected = cellResizeRejectedSizes(state.cellResizeRejected, state.currentDelivery?.parcelID);
  createInlineAlert({
    id: CELL_RESIZE_ALERT_ID,
    host: CELL_ALERT_HOST_ID,
    compact: true,
    tone: 'warn',
    title: CELL_RESIZE_REJECTED_TITLE,
    message: cellResizeRejectedHint(rejected, freeCellsView(state.freeCells)),
    actions: [
      { label: 'Повідомити проблему', className: 'inline-alert-btn-problem', onClick: () => { try { openProblemMenuFor('delivery'); } catch (_) {} } },
    ],
  });
}

async function pickupOpen() {
  if (!state.currentPickup) return;
  clearCellWarn();
  try {
    await ensureCourierContext();
    const body = { BranchIDRef: state.branchRef, CourierIDRref: state.courier.courierIDSQL, ShipmentIDRref: state.currentPickup.parcelID, parcelAgent: parcelAgentForRequest(state.currentPickup), client: LOCKER_CLIENT_APP_MIN1 };
  const r = await withOperation(() => api('/locker/remove', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), loaderText: 'Відкриваємо комірку…' }));
  const lockerErr = showLockerError(r, 'locker_remove');
  if (lockerErr) throw lockerErrorException(lockerErr);
    // Show details actions, enable resend
    document.getElementById('pickup_details')?.classList.remove('hidden');
    document.getElementById('pickup_actions')?.classList.remove('hidden');
    document.getElementById('btn_pickup_resend')?.classList.remove('hidden');
    const trans = extractTransId(r);
    if (!trans) {
      // 1С відповіла успіхом, але ідентифікатора транзакції не дала: підтвердити
      // забір буде НІЧИМ. Робочий стан тут показувати не можна — ведемо в те
      // саме попередження, що й при обірваному запиті, з виходом у «Проблему».
      state.currentPickupTrans = null;
      log('pickup:open_no_transid parcel=' + (state.currentPickup?.parcelID || ''));
      warnCellMayBeOpen('pickup', () => pickupResend());
      return;
    }
    state.currentPickupTrans = trans;
    notePostamatAnswered();
    log('Open trans=' + state.currentPickupTrans);
  } catch (e) {
    log('Помилка open ' + e.message);
    if (e && e.lockerCode === LOCKER_CODE_NOT_OPENED) {
      state.currentPickupTrans = null;
      document.getElementById('pickup_details')?.classList.remove('hidden');
      document.getElementById('pickup_actions')?.classList.remove('hidden');
      document.getElementById('btn_pickup_resend')?.classList.remove('hidden');
      warnCellNotOpened('pickup', e.message, () => pickupResend());
    } else if (isCellMayBeOpenError(e)) {
      // Cell may be physically open despite no confirmation — guide a re-open.
      document.getElementById('pickup_details')?.classList.remove('hidden');
      document.getElementById('pickup_actions')?.classList.remove('hidden');
      document.getElementById('btn_pickup_resend')?.classList.remove('hidden');
      warnCellMayBeOpen('pickup', () => pickupResend());
    } else if (shouldShowGenericOpenError(e)) {
      // Ділову причину від 1С уже показано — другий тост лише перекрив би її.
      toast('Не вдалося відкрити комірку. Спробуйте ще раз.');
    }
  }
}
function pickupResend() {
  pickupOpen();
}
function pickupManual() {
  // Show manual panel within details context only
  document.getElementById('pickup_manual_panel')?.classList.remove('hidden');
  const inp = document.getElementById('pickup_manual_input');
  if (inp) { inp.value = ''; inp.focus(); }
}
function isMiniSticker(code) {
  return /^[0-9]{7}$/.test(code);
}
async function pickupScan() {
  if (!qrSupported || !tg?.showScanQrPopup) {
    log('qr:unsupported qrSupported=' + qrSupported + ' showScanQrPopup=' + (typeof tg?.showScanQrPopup));
    toast('Сканер недоступний у цій версії Telegram');
    return;
  }
  log('qr:starting_pickup_scan');
  try {
    tg.showScanQrPopup({ text: 'Скануйте ШК/стікер' }, (res) => {
      if (!res) return;
      const code = res.data || res;
      try {
        tg.closeScanQrPopup();
      } catch (_) {}
      setTimeout(() => {
        try {
          tg.closeScanQrPopup();
        } catch (_) {}
      }, 120);
      handlePickupCode(code);
    });
  } catch (error) {
    log('qr:showScanQrPopup_error (pickup): ' + (error?.message || error));
    toast('Помилка відкриття сканера');
  }
}
async function pickupScanTsdAware() {
  if (tsdActive()) {
    toast('Увімкнено режим ТЗД');
    return;
  }
  return pickupScan();
}
async function handlePickupCode(code) {
  log('Код=' + code);
  await pickupConfirm(code, false);
}
async function pickupManualSubmit() {
  const el = document.getElementById('pickup_manual_input');
  const v = (el?.value || '').trim();
  if (!v) return;
  await pickupConfirm(v, true);
}
async function pickupSkip() {
  await pickupConfirm(null, true);
}
async function pickupConfirm(scanned, manual) {
  const current = state.currentPickup;
  if (!current) return;
  // Guard: a confirm needs a real transID issued by a successful open. If the
  // open failed (parcel not in locker / cell didn't open) there is no transID,
  // and sending "0" only earns a misleading 1C error 13. Make the courier
  // re-open the cell instead.
  if (!state.currentPickupTrans) {
    toast('Немає підтвердження відкриття комірки. Відкрийте її ще раз.', LOCKER_ERROR_TOAST_MS);
    log('pickupConfirm blocked: no transID (open failed or not run)');
    return;
  }
  // Див. deliveryConfirm: тост про фейл — лише до успішної відповіді сервера.
  let confirmOk = false;
  try {
    await ensureCourierContext();
    if (scanned && isMiniSticker(scanned))
      await withOperation(() => api('/mini-sticker', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ parcelID: current.parcelID, number: scanned }), loaderText: 'Готуємо стікер…' }));
    const body = { BranchIDRef: state.branchRef, CourierIDRref: state.courier.courierIDSQL, ShipmentIDRref: current.parcelID, transID: state.currentPickupTrans, parcelAgent: parcelAgentForRequest(current), client: LOCKER_CLIENT_APP_MIN1 };
  const removeConfirmResp = await withOperation(() => api('/locker/remove/confirm', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), loaderText: 'Підтвердження…' }));
  const lockerErr = showLockerError(removeConfirmResp, 'locker_remove_confirm');
  if (lockerErr) throw lockerErrorException(lockerErr);
  confirmOk = true;
  clearCellWarn();
    state.processedPickup.add(current.parcelID);
    // Забір фізично звільнив комірку, але ЯКОГО розміру — ми не знаємо, а
    // знімок знято ще до забору. Нулі з нього перестають бути знанням: інакше
    // на повному поштоматі курʼєр, який щойно вийняв пʼять посилок, побачив би
    // «відкривати нема чого» і пішов створювати заявку на власноруч звільнене
    // місце. Нового запиту в 1С це не коштує — див. markFreeCellsStale.
    state.freeCells = markFreeCellsStale(state.freeCells);
    state.currentPickup = null;
    state.currentPickupTrans = null;
    // Return to list view and clear details
    document.getElementById('pickup_details')?.classList.add('hidden');
    document.getElementById('pickup_list')?.classList.remove('hidden');
    document.getElementById('pickup_to_delivery_wrap')?.classList.remove('hidden');
    document.getElementById('pickup_parcel_num')?.replaceChildren();
    const bcEl = document.getElementById('pickup_parcel_barcode');
    if (bcEl) bcEl.textContent = '';
    resetPickupScanPanel();
    hidePickupProblem();
    renderPickupList();
    updateProgress();
    simulator.notify('pickup-confirmed', { parcelID: current.parcelID, manual: !!manual, scanned: !!scanned });
    if (allPickupDone()) {
      simulator.notify('pickup-finished', { mode: 'confirm' });
      maybeSwitchToDelivery();
    }
  } catch (e) {
    log('Помилка confirm ' + e.message);
    // Той самий принцип, що і в доставці: курʼєр має бачити, ЧОМУ не пройшло.
    // Але якщо ділову причину від 1С уже показано — другий тост лише перекриє
    // її і порадить повтор там, де повтор нічого не змінить.
    if (!confirmOk && shouldShowGenericOpenError(e)) {
      toast('Підтвердження не пройшло: ' + (e.message || 'помилка') +
            '. Посилка лишилась у списку — спробуйте ще раз.', LOCKER_ERROR_TOAST_MS);
    }
  }
}
function allPickupDone() {
  return state.pickup.filter((p) => !state.skippedPickup.has(p.parcelID)).every((p) => state.processedPickup.has(p.parcelID));
}
function updateProgress() {
  const total = state.pickup.length;
  const done = [...state.processedPickup].length;
  const pp = document.getElementById('progress_pickup');
  if (pp) pp.textContent = `Забрано ${done}/${total}`;
  // keep deliveryTotal in sync with state snapshot
  deliveryTotal = state.deliveryTotal || state.deliveryTotal === 0 ? state.deliveryTotal : state.delivery.length;
  const delivered = deliveryTotal - state.delivery.length;
  const pd = document.getElementById('progress_delivery');
  if (pd) pd.textContent = `Вкладено ${delivered}/${deliveryTotal}`;
  // Єдиний чіп проблем сесії: лише нові проблеми цієї сесії (pickup + delivery), без problemCountBase
  const sessionProblems = (state.skippedDelivery ? state.skippedDelivery.size : 0) + (state.skippedPickup ? state.skippedPickup.size : 0);
  const ps = document.getElementById('progress_session_problems');
  if (ps) {
    if (sessionProblems > 0) {
      ps.textContent = `Проблемні ${sessionProblems}`;
      ps.classList.remove('hidden');
    } else {
      ps.classList.add('hidden');
    }
  }
}
async function updateCellsInfo() {
  const el = document.getElementById('cells_info');
  if (!el) return;
  // Чіп не знає розмірів комірок і не оновлюється від входу в поштомат до
  // виходу, тому й підписаний чесно — див. freeCellsLabel.
  // Пояснення не можна лишати самому лише в title: у Telegram WebApp на
  // телефоні наведення немає, тож tooltip курʼєр не побачить НІКОЛИ, а саме це
  // непорозуміння («вільні комірки є, а вкласти не виходить») дає скарги.
  // Тому чіп ще й натискається.
  if (!el.dataset.hintBound) {
    el.dataset.hintBound = '1';
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.addEventListener('click', () => {
      const breakdown = freeCellsSummary(state.freeCells);
      if (breakdown) {
        const at = freeCellsActualLabel(state.freeCells);
        toast(at ? `${breakdown} — дані на ${at}` : breakdown, LOCKER_ERROR_TOAST_MS);
      } else {
        toast(FREE_CELLS_HINT, LOCKER_ERROR_TOAST_MS);
      }
      // Свідоме оновлення курʼєром — єдина причина спитати 1С удруге за візит
      // (не частіше, ніж раз на пів хвилини, див. FREE_CELLS_MANUAL_REFRESH_MS).
      refreshFreeCells({ manual: true }).catch(() => {});
    });
  }
  const apply = (info) => {
    const view = freeCellsLabel(info);
    el.textContent = view.text;
    if (view.title) el.title = view.title; else el.removeAttribute('title');
  };
  if (!state.branchRef) {
    apply(null);
    return;
  }
  // Якщо є розбивка за розмірами — показуємо суму саме з неї: вона свіжіша за
  // postamatInfo (зменшується після кожного вкладення) і по тику розкладається
  // на розміри. Підпис при цьому ІНШИЙ: розбивка доїжджає за ~400 мс після
  // сканування і замінює число з postamatInfo, а мовчазна заміна числа під тим
  // самим підписом читається як «воно стрибнуло саме».
  const total = freeCellsTotal(state.freeCells);
  if (total !== null) {
    el.textContent = `Вільних за розмірами: ${total}`;
    el.removeAttribute('title');
    return;
  }
  try {
    // Prefer fresh info from last parcels response
    if (state.postamatInfo && (typeof state.postamatInfo.freeCells === 'number' || typeof state.postamatInfo.totalCells === 'number')) {
      apply(state.postamatInfo);
      return;
    }
    // Нейтральний плейсхолдер: /locker/cells віддає ЗАГАЛЬНУ кількість комірок,
    // тож обіцяти тут «вільних» не можна — підпис на очах міняється з
    // «Вільних усього: …» на «Комірок усього: 40».
    el.textContent = 'Комірки: …';
    const data = await api(`/locker/cells?branchIDRef=${encodeURIComponent(state.branchRef)}`, { loaderText: 'Оновлення даних…' });
    apply(data && typeof data.total === 'number' ? { totalCells: data.total } : null);
  } catch (e) {
    apply(null);
    log('cells err ' + e.message);
  }
}
function maybeSwitchToDelivery() {
  if (allPickupDone()) {
    // Block delivery if branch is closed
    if (!state.branchIsOpen) {
      toast('Підрозділ зачинено. Вкладення посилок заборонено.');
      stopPostamatOnline('done');
      show('screen_done');
      return;
    }
    show('screen_delivery');
  // ensure list renders immediately after auto-switch
  renderDeliveryList();
  updateProgress();
    if (state.delivery.length && !tsdActive()) setTimeout(() => deliveryScan(), 150);
  }
}

function resetPickupProblemNote() {
  state.pendingPickupProblem = null;
  const wrap = document.getElementById('pickup_problem_note_wrap');
  wrap?.classList.add('hidden');
  const input = document.getElementById('pickup_problem_note');
  if (input) input.value = '';
}

function promptPickupProblemNote(code, requiresNote) {
  state.pendingPickupProblem = { code, requiresNote: !!requiresNote };
  const wrap = document.getElementById('pickup_problem_note_wrap');
  wrap?.classList.remove('hidden');
  const input = document.getElementById('pickup_problem_note');
  if (input) {
    input.value = '';
    input.focus();
  }
}

function showPickupProblem() {
  resetPickupProblemNote();
  document.getElementById('pickup_problem_menu')?.classList.remove('hidden');
}
function hidePickupProblem() {
  document.getElementById('pickup_problem_menu')?.classList.add('hidden');
  resetPickupProblemNote();
}
async function sendProblem(type, note) {
  const current = state.currentPickup;
  if (!current) return;
  state.pendingPickupProblem = null;
  try {
    await api('/locker/problem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        parcelID: current.parcelID || null,
        parcelNumber: current.parcelNumber || null,
        parcelBarcode: current.barcode || null,
        problemType: type,
        note,
        branchIDRef: state.branchRef,
        branchFreeCells: typeof state.postamatInfo?.freeCells === 'number' ? state.postamatInfo.freeCells : null,
        parcelAgent: parcelAgentForRequest(current),
      }),
      loaderText: 'Надсилання…',
    });
    state.skippedPickup.add(current.parcelID);
    state.pickupProblemTypes = state.pickupProblemTypes || {};
    state.pickupProblemTypes[current.parcelID] = type;
    log('pickup_problem:mark ' + current.parcelID + ' type=' + type);
    const pid = current.parcelID;
    state.currentPickup = null;
    state.currentPickupTrans = null;
    document.getElementById('pickup_actions')?.classList.add('hidden');
    document.getElementById('pickup_details')?.classList.add('hidden');
    document.getElementById('pickup_list')?.classList.remove('hidden');
    document.getElementById('pickup_to_delivery_wrap')?.classList.remove('hidden');
    resetPickupScanPanel();
    hidePickupProblem();
    renderPickupList();
    updateProgress();
    simulator.notify('pickup-confirmed', { parcelID: pid, problem: type, manual: false, scanned: false });
    if (allPickupDone()) {
      simulator.notify('pickup-finished', { mode: 'problem' });
      maybeSwitchToDelivery();
    }
    showUndoToast('Проблему збережено', () => {
      state.skippedPickup.delete(pid);
      delete state.pickupProblemTypes[pid];
      log('pickup_problem:undo ' + pid);
      renderPickupList();
      updateProgress();
    });
  } catch (e) {
    log('Помилка problem ' + e.message);
  }
}

function toDelivery() {
  // Block delivery if branch is closed
  if (!state.branchIsOpen) {
    toast('Підрозділ зачинено. Вкладення посилок заборонено.');
    return;
  }
  show('screen_delivery');
  renderDeliveryList();
  showDeliveryMenus();
  if (state.delivery.length && !tsdActive()) setTimeout(() => deliveryScan(), 150);
}
function backToPickup() {
  show('screen_pickup');
  // Ensure list is visible, details hidden
  document.getElementById('pickup_details')?.classList.add('hidden');
  document.getElementById('pickup_list')?.classList.remove('hidden');
}
async function deliveryScan() {
  // Block delivery scan if branch is closed
  if (!state.branchIsOpen) {
    toast('Підрозділ зачинено. Вкладення посилок заборонено.');
    return;
  }
  if (tsdActive()) {
    toast('Увімкнено режим ТЗД');
    return;
  }
  if (!qrSupported || !tg?.showScanQrPopup) {
    log('qr:unsupported qrSupported=' + qrSupported + ' showScanQrPopup=' + (typeof tg?.showScanQrPopup));
    toast('Сканер недоступний у цій версії Telegram');
    return;
  }
  if (scanInProgress) {
    log('qr:scan_already_in_progress - forcing reset');
    scanInProgress = false;
    if (scanGuard) {
      clearTimeout(scanGuard);
      scanGuard = null;
    }
    // Не повертаємося, продовжуємо скан після reset
  }
  log('qr:starting_delivery_scan');
  scanInProgress = true;
  hideDeliveryMenus();
  if (scanGuard) {
    clearTimeout(scanGuard);
    scanGuard = null;
  }
  scanGuard = setTimeout(() => {
    if (scanInProgress) {
      log('qr:timeout_reset scanInProgress');
      scanInProgress = false;
      showDeliveryMenus();
    }
  }, 2500);
  
  try {
    tg.showScanQrPopup({ text: 'Відскануйте QR-код посилки для вкладення' }, (res) => {
    try {
      tg.closeScanQrPopup();
    } catch (_) {}
    setTimeout(() => {
      try {
        tg.closeScanQrPopup();
      } catch (_) {}
    }, 120);
    const data = res && typeof res === 'object' ? res.data : res;
    if (!data) {
      scanInProgress = false;
      if (scanGuard) {
        clearTimeout(scanGuard);
        scanGuard = null;
      }
      showDeliveryMenus();
      return;
    }
    startDeliveryFor(data);
    scanInProgress = false;
    if (scanGuard) {
      clearTimeout(scanGuard);
      scanGuard = null;
    }
  });
  } catch (error) {
    log('qr:showScanQrPopup_error: ' + (error?.message || error));
    scanInProgress = false;
    if (scanGuard) {
      clearTimeout(scanGuard);
      scanGuard = null;
    }
    showDeliveryMenus();
    toast('Помилка відкриття сканера');
  }
}
function startDeliveryFor(code) {
  if (!state.branchIsOpen) {
    toast('Підрозділ зачинено. Вкладення посилок заборонено.');
    return;
  }
  const parcelID = guessDeliveryParcel(code);
  if (!parcelID) {
    // Раніше тут був мовчазний вихід: сканер закривався і не відбувалось нічого.
    // Кур'єр не міг відрізнити «сканер не спрацював» від «посилки тут немає»
    // і повідомляв про несправність поштомату. Показуємо, що саме не збіглося.
    log('Не знайдено посилку для скану: ' + code);
    toast(scanMissMessage(code));
    showDeliveryMenus();
    return;
  }
  const full = (state.delivery || []).find(p => p.parcelID === parcelID) || {};
  selectDelivery({ ...full, parcelID });
}
// Ліміту спроб відкриття немає (рішення власника 16.10): автоперебір розмірів
// іде лише вгору XS→XL і зупиняється сам, а кожна наступна спроба — це вже
// свідомий тап курʼєра (розмір, «Відкрити повторно»), який ліміт лише блокував.
async function attemptDeliveryOpen() {
  if (!state.currentDelivery) return;
  // Єдина брама до /locker/insert: сюди ведуть вибір розміру, «Відкрити
  // повторно», автовідкриття і «Відкрити ще раз» з плашок. Посилка могла
  // пройти selectDelivery, коли блоку ще не було (напр. «Undo» «Проблеми»
  // посилки Епіцентру) — тому перевіряємо і тут, перед запитом.
  const epBlock = epFirstBlock(state.currentDelivery);
  if (epBlock) {
    showEpFirstAlert(epBlock);
    document.getElementById('delivery_actions')?.classList.remove('hidden');
    return;
  }
  clearCellWarn();
  state.currentDeliveryAttempts = (state.currentDeliveryAttempts || 0) + 1;
  highlightSizeButton(state.currentCellSize);
  hideDeliveryMenus();
  // Update loader text with current size so courier sees progress
  const sizeLabel = state.currentCellSize || 'S';
  const attemptNum = state.currentDeliveryAttempts;
  if (attemptNum > 1) {
    updateLoaderText(`Пробуємо комірку ${sizeLabel}…`);
  }
  try {
    await ensureCourierContext();
    // Розмір їде в 1С такий, який обрав курʼєр: перетворення XL→L скасовано у
    // 1.9.1 (метод відкриття приймає і XS, і XL). cellSizeUi лишається рівно
    // тим самим значенням — поле тримаємо заради неперервності журналу.
    const body = { BranchIDRef: state.branchRef, CourierIDRref: state.courier.courierIDSQL, ShipmentIDRref: state.currentDelivery.parcelID, CellSize: state.currentCellSize, cellSizeUi: state.currentCellSize, parcelAgent: parcelAgentForRequest(state.currentDelivery), client: LOCKER_CLIENT_APP_MIN1 };
  const r = await withOperation(() => api('/locker/insert', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), noLoader: true }));
  const lockerErr = showLockerError(r, 'locker_insert');
  if (lockerErr) {
    noteSizeRejected(lockerErr, state.currentCellSize);
    throw lockerErrorException(lockerErr);
  }
    const trans = extractTransId(r);
    if (!trans) {
      // Глухий кут: 1С відповіла успіхом, але без ідентифікатора транзакції —
      // резервація не створюється і підтвердити вкладення неможливо в принципі.
      // Раніше екран показував зелений стан «можна підтверджувати»: курʼєр тиснув
      // «Підтвердити», отримував відмову і відкривав комірку знову (у середньому
      // 4,9 відкриття на посилку, рекорд — 34 за дві хвилини). Тепер одразу
      // ведемо в попередження про можливо відкриту комірку з виходом у «Проблему».
      state.currentDeliveryTrans = null;
      document.getElementById('delivery_actions')?.classList.remove('hidden');
      resetDeliveryConfirmButton();
      document.getElementById('delivery_cell_label')?.classList.add('hidden');
      log('insert:no_transid size=' + state.currentCellSize + ' parcel=' + (state.currentDelivery?.parcelID || ''));
      // «Відкрити ще раз» — реальний courierInsert у 1С; вихід у «Проблему»
      // є в самому попередженні.
      warnCellMayBeOpen('delivery', () => attemptDeliveryOpen());
      return;
    }
    state.currentDeliveryTrans = trans;
    // Комірку відкрито — бронь посилки тепер ця. Старі відмови «змінити розмір»
    // відлічуємо наново: інакше розмір, що колись не відкрився, лишався б
    // заблокованим для «Відкрити повторно» до кінця роботи з посилкою.
    state.cellResizeRejected = null;
    notePostamatAnswered();
    // Мітку розміру показуємо ВСІМ, зокрема Епіцентру: курʼєр має бачити, яку
    // саме комірку йому відкрили, інакше «більший розмір» ні з чим порівняти.
    const cellLbl = document.getElementById('delivery_cell_label');
    if (cellLbl) {
      // Габарити (см) показуємо саме тут, а не на кнопках сітки: це єдина мить,
      // коли вони потрібні — курʼєр з посилкою в руці дивиться на відкриту
      // комірку і вирішує, класти чи брати більшу. Пʼять підписів із трьома
      // числами в сітці той самий екран лише захаращують, а наведення в
      // Telegram WebApp немає взагалі, тож tooltip курʼєр не побачив би.
      const dims = cellDimsForSize(state.currentCellSize);
      cellLbl.textContent = dims
        ? `Комірка ${state.currentCellSize} · ${dims}`
        : `Комірка ${state.currentCellSize}`;
      cellLbl.classList.remove('hidden');
    }
    markDeliveryConfirmReady();
    document.getElementById('delivery_actions')?.classList.remove('hidden');
    log('Insert open size=' + state.currentCellSize + ' trans=' + state.currentDeliveryTrans);
  } catch (e) {
    log('Insert fail size=' + state.currentCellSize + ' ' + e.message);
    if (isCellMayBeOpenError(e)) {
      // Ambiguous open (timeout / server error): the cell may have physically
      // opened. Do NOT auto-progress sizes — that could open a second cell.
      // Warn the courier not to put the parcel in, and offer a clean re-open.
      document.getElementById('delivery_actions')?.classList.remove('hidden');
      resetDeliveryConfirmButton();
      document.getElementById('delivery_cell_label')?.classList.add('hidden');
      warnCellMayBeOpen('delivery', () => { state.currentDeliveryAttempts = 0; attemptDeliveryOpen(); });
      return;
    }
    if (e && e.lockerCode === LOCKER_CODE_NOT_OPENED) {
      // Поштомат не на звʼязку: будь-який інший розмір дасть те саме, а кожна
      // спроба — ще одна бронь у 1С. Без перебору — попередження і вибір курʼєра.
      // transID попередньої спроби вже мертвий (бекенд записав бронь 25 новішою):
      // «Проблема» без нього знайде на сервері саме бронь 25 і скасує її одразу.
      state.currentDeliveryTrans = null;
      document.getElementById('delivery_actions')?.classList.remove('hidden');
      resetDeliveryConfirmButton();
      document.getElementById('delivery_cell_label')?.classList.add('hidden');
      warnCellNotOpened('delivery', e.message, () => attemptDeliveryOpen());
      return;
    }
    // Посилку Епіцентру з бронню не перебираємо: більший розмір 1С трактує як
    // зміну броні і в повному поштоматі відмовляє (20 «більшого розміру»).
    if (e && (!allowsAutoSizeEscalation(e.lockerCode) || state.currentDeliveryBooked)) {
      // 13 (рішення власника 26.10): збій 1С або відмова Епіцентру змінити розмір
      // броні. Перебір S→M→L→XL тут лише множив відмови — до 4 запитів на тап, до
      // 61 на посилку. Причину вже показано (тост або плашка), розмір обирає курʼєр.
      document.getElementById('delivery_actions')?.classList.remove('hidden');
      resetDeliveryConfirmButton();
      document.getElementById('delivery_cell_label')?.classList.add('hidden');
      if (isCellResizeRejected(e.lockerCode, e.lockerDetails)) {
        // Запамʼятовуємо відхилений розмір: «Відкрити повторно» його більше не
        // шле (deliveryRetry), а порада його називає і не радить.
        state.cellResizeRejected = rememberCellResizeRejected(
          state.cellResizeRejected, state.currentDelivery?.parcelID, state.currentCellSize,
        );
        warnCellResizeRejected();
      }
      log('insert:no_escalation code=' + e.lockerCode + ' size=' + state.currentCellSize);
      return;
    }
    // Наступний розмір: коли дані про вільні комірки є, ескалація пропускає
    // розміри з явним нулем — інакше кожен із них це гарантована відмова 1С.
    const next = nextSizeForAgent(state.currentDelivery?.agent || null, state.currentCellSize, state.freeCells);
    if (next && next !== state.currentCellSize) {
      state.currentCellSize = next;
      highlightSizeButton(state.currentCellSize);
      attemptDeliveryOpen();
    } else {
      // No further automatic progression; let user decide (retry/other size)
      document.getElementById('delivery_actions')?.classList.remove('hidden');
      resetDeliveryConfirmButton();
      document.getElementById('delivery_cell_label')?.classList.add('hidden');
      log('Немає доступних комірок для розміру ' + state.currentCellSize);
    }
  }
}
async function deliveryConfirm() {
  const current = state.currentDelivery;
  if (!current) return;
  // Guard: confirm needs a real transID from a successful cell open. Without it
  // (open failed — e.g. no free cells) sending "0" only triggers 1C error 13.
  if (!state.currentDeliveryTrans) {
    toast('Немає підтвердження відкриття комірки. Не кладіть посилку, відкрийте комірку ще раз.', LOCKER_ERROR_TOAST_MS);
    log('deliveryConfirm blocked: no transID (open failed or not run)');
    return;
  }
  // Тост про фейл показуємо ЛИШЕ якщо не дійшли до успішної відповіді:
  // catch нижче ловить і винятки UI-коду ПІСЛЯ успішного confirm — казати
  // курʼєру «не пройшло», коли вкладення зараховано, не можна.
  let confirmOk = false;
  try {
    await ensureCourierContext();
    const body = { BranchIDRef: state.branchRef, CourierIDRref: state.courier.courierIDSQL, ShipmentIDRref: current.parcelID, transID: state.currentDeliveryTrans, parcelAgent: parcelAgentForRequest(current), client: LOCKER_CLIENT_APP_MIN1 };
  const confirmResp = await withOperation(() => api('/locker/insert/confirm', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), loaderText: 'Підтвердження…' }));
  const lockerErr = showLockerError(confirmResp, 'locker_insert_confirm');
  if (lockerErr) throw lockerErrorException(lockerErr);
  confirmOk = true;
  clearCellWarn();
  // Confirm пізніше 10-хв авто-скасування: сервер уже повідомив процесінг, що
  // комірка порожня. Мовчати не можна — посилку в комірці можуть не видати.
  if (confirmResp && confirmResp.reservation_warning) {
    // Це не transient-подія: після цього тексту курʼєр мусить піти в підтримку,
    // інакше посилку в комірці можуть не видати. Тостом його не прочитати —
    // 154 символи за 1.6 с, — тому стійка плашка, як у warnCellMayBeOpen.
    createInlineAlert({
      id: 'alert_late_confirm',
      tone: 'warn',
      title: 'Підтвердження надійшло із запізненням',
      message: confirmResp.reservation_warning,
    });
    log('late confirm: ' + confirmResp.reservation_warning);
  }
    // Лічильник вільних комірок зменшуємо ЛОКАЛЬНО, без нового запиту до 1С:
    // ми самі щойно зайняли комірку і знаємо, якого вона розміру. Перепитувати
    // поштомат після кожного вкладення означало б 12-14 тисяч викликів на добу
    // замість обіцяних Meest ≈1 700.
    state.freeCells = decrementFreeCell(state.freeCells, state.currentCellSize);
    // Вкладена посилка опрацьована в цьому візиті — навіть якщо 1С ще віддасть
    // її в списку після нового сканування QR, блоку «спершу Епіцентр» вона не тримає.
    noteHandledInVisit(epFirstVisits(), state.branchRef, current.parcelID, 'inserted');
    saveEpFirstVisits();
    state.delivery = state.delivery.filter((p) => p.parcelID !== current.parcelID);
    state.currentDelivery = null;
    state.currentDeliveryTrans = null;
    state.currentDeliveryAttempts = 0;
  document.getElementById('delivery_actions')?.classList.add('hidden');
  resetDeliveryConfirmButton();
  highlightSizeButton(null);
  renderSizeGrid();
  updateCellsInfo();
  document.getElementById('delivery_cell_label')?.classList.add('hidden');
  // Clear header meta after confirming
  clearDeliveryMeta();
    // Ensure list/controls are visible after successful confirm
    showDeliveryMenus();
    renderDeliveryList();
    updateProgress();
  // Після завершення конкретної посилки знову показуємо кнопку назад (вона тепер у списку)
  document.getElementById('btn_delivery_back')?.classList.remove('hidden');
    simulator.notify('delivery-confirmed', { parcelID: current.parcelID });
    if (!state.delivery.length) {
      log('Вкладення завершено');
      simulator.notify('delivery-finished', { remaining: 0 });
      stopPostamatOnline('finish');
      finish();
      simulator.notify('session-finished');
    } else {
      if (!tsdActive()) deliveryScan();
    }
  } catch (e) {
    log('Confirm err ' + e.message);
    // Курʼєр стоїть перед відкритою коміркою: мовчазний фейл лишав його без
    // відповіді, зарахувалось вкладення чи ні. Показуємо текст від сервера —
    // крім випадку, коли ділову причину від 1С уже показано окремим тостом.
    if (!confirmOk && shouldShowGenericOpenError(e)) {
      toast('Підтвердження не пройшло: ' + (e.message || 'помилка') +
            '. Посилка лишилась у списку — спробуйте ще раз.', LOCKER_ERROR_TOAST_MS);
    }
  }
}
function deliveryRetry() {
  // Той самий гейт, що й на кнопках сітки: «Відкрити повторно» з розміром,
  // про який 1С щойно сказала «вільних немає», — це ще один гарантовано
  // відмовний courierInsert. Сітка нулі блокує, повтор блокував їх теж.
  const view = freeCellsView(state.freeCells);
  if (!state.currentDeliveryBooked && isSizeBlocked(view, state.currentCellSize)) {
    toast('Комірок розміру ' + state.currentCellSize + ' немає — оберіть інший розмір', LOCKER_ERROR_TOAST_MS);
    focusFirstAvailableSize(view);
    return;
  }
  // Розмір, який 1С для цієї посилки щойно відхилила з 13 «змінити розмір»:
  // повтор дає ту саму відмову (на проді 21–25.10 — 6 успіхів із ~1 600 повторів
  // протягом години), а плашка просить його не повторювати. Запиту не шлемо —
  // підказуємо інший розмір. Тап по літері в сітці лишається вибором курʼєра.
  const rejected = cellResizeRejectedSizes(state.cellResizeRejected, state.currentDelivery?.parcelID);
  if (rejected.includes(state.currentCellSize)) {
    toast(cellResizeRetryBlockedText(state.currentCellSize), LOCKER_ERROR_TOAST_MS);
    const size = suggestCellResizeSize(rejected, view);
    if (size) document.querySelector(`#size_selector .size-btn[data-size="${size}"]`)?.focus?.();
    log('insert:retry_blocked_resize size=' + state.currentCellSize);
    return;
  }
  attemptDeliveryOpen();
}

async function resolveLocker(url) {
  // Без жодного ідентифікатора кур'єра всі подальші запити безглузді:
  // /locker/parcels віддасть порожній список без помилки, а перша ж
  // спроба відкрити комірку впаде на /courier/context. Кажемо прямо.
  if (!hasCourierIdentity(window.location.search)) {
    log('resolveLocker:missing_identity');
    toast('Відсутній ідентифікатор кур’єра. Відкрийте застосунок з меню бота.');
    return false;
  }
  let resolved = false;
  try {
    await runWithLoader(async () => {
      // flows.resolveLockerUrl тепер сам вимагає courier_meest_id і використовує api() автопараметр
      const ctx = await api(`/locker/by-qr?url=${encodeURIComponent(url)}`, { noLoader: true });
      if (!ctx.found) {
        log('Поштомат не знайдено');
        notifyLockerNotFound(ctx.errorMessage);
        return;
      }
      // Новий поштомат — опитування звʼязку попереднього зупиняємо, плашку знімаємо.
      resetPostamatOnline('new_scan');
      // Плашка про комірку (25 / «могла відкритись») стосувалась попереднього
      // поштомата: на новому вона хибна, ховала б його червону «не на звʼязку»
      // і тримала б isCellStepCritical() без причини.
      clearCellWarn();
      // Плашка «спершу Епіцентр» — про список попереднього поштомата.
      document.getElementById(EP_FIRST_ALERT_ID)?.remove();
      await applyLockerCtx(ctx);
      // Мережа точки (meest / epicentr / rozetka / unknown): метод звʼязку 1С
      // знає лише наші поштомати — для чужих бекенд навіть не питаємо. Вона ж
      // вмикає правило «спершу посилки Епіцентру» (лише 'epicentr').
      state.branchNetworkId = ctx.branch?.networkId || null;
      // Новий поштомат — кількості з попереднього недійсні.
      state.freeCells = null;
      state.freeCellsFetchedAt = 0;
      // Звʼязок поштомата — окремо і без await, паралельно з посилками.
      checkPostamatOnline();
      document.getElementById('screen_start')?.classList.add('hidden');
      log('Resolved branch ' + state.branchID);
    simulator.notify('locker-resolved', { branchID: state.branchID, branchRef: state.branchRef, source: 'scan' });
      resolved = true;
  const parcelsData = await loadParcels(undefined, true, { noLoader: true });
  // Що курʼєр уже вклав / по чому подав «Проблему» тут сьогодні — ДО першого
  // малювання списку, інакше після перезавантаження блок «спершу Епіцентр»
  // блимнув би по вже опрацьованих посилках.
  applyEpFirstFromServer(parcelsData);
  // immediately render lists and chips
  deliveryTotal = state.deliveryTotal;
  renderPickupList();
  renderDeliveryList();
  updateProgress();
      await updateCellsInfo();
      // Довідка про вільні комірки за розмірами — ОКРЕМИМ викликом і БЕЗ await:
      // екран має бути готовий одразу, а кількості доїдуть за ~400 мс. Це
      // єдине автоматичне звернення до методу за візит курʼєра до поштомата.
      refreshFreeCells().catch((e) => log('free_cells:skip ' + (e?.message || e)));
      const inventoryTasks = await loadInventoryTasks({ silent: true });
      const pendingInventory = state.inventoryPendingCount || 0;
      if (pendingInventory > 0 && Array.isArray(inventoryTasks) && inventoryTasks.length) {
        toast(`Є ${pendingInventory} завдань інвентаризації, оновіть статуси`);
        await openInventoryPanel({ reload: false, silent: true });
      }
    }, 'Пошук поштомату…');
  } catch (e) {
    log('Помилка resolve ' + e.message);
    return false;
  }
  if (resolved) {
    autoScanCompleted = true;
    cancelAutoScanTimer();
    autoScanAttempts = 0;
  }
  return resolved;
}

function cancelAutoScanTimer() {
  if (autoScanTimer) {
    clearTimeout(autoScanTimer);
    autoScanTimer = null;
  }
}

function scheduleAutoScanRetry(delay = 400, { force = false } = {}) {
  if (force) {
    autoScanAttempts = 0;
    autoScanCompleted = false;
  }
  if (autoScanCompleted || state.branchID || !qrSupported || tsdActive()) return;
  if (!force && autoScanAttempts >= AUTO_SCAN_MAX_ATTEMPTS) {
    log('qr:auto_scan_limit_reached');
    return;
  }
  cancelAutoScanTimer();
  autoScanTimer = setTimeout(async () => {
    autoScanTimer = null;
    if (autoScanCompleted || state.branchID || !qrSupported || tsdActive()) return;
    if (autoScanPopupOpen) {
      scheduleAutoScanRetry(800);
      return;
    }
    autoScanAttempts += 1;
    log('qr:auto_scan_attempt ' + autoScanAttempts);
    const ok = await scanLocker({ auto: true });
    if (!ok && autoScanAttempts < AUTO_SCAN_MAX_ATTEMPTS) {
      scheduleAutoScanRetry(Math.min(2000, 600 * autoScanAttempts));
    }
  }, Math.max(0, delay));
}

async function scanLocker({ auto = false } = {}) {
  ensureWebAppExpanded();
  if (!tg?.showScanQrPopup) {
    log('QR API недоступний');
    if (auto) {
      toast('Автосканер недоступний у цій версії Telegram. Натисніть «Сканувати» вручну.', LOCKER_ERROR_TOAST_MS);
      autoScanCompleted = true;
      cancelAutoScanTimer();
    }
    return false;
  }
  if (auto) {
    if (autoScanPopupOpen) {
      log('qr:auto_scan_popup_active');
      return true;
    }
  } else {
    autoScanCompleted = true;
    cancelAutoScanTimer();
  }
  try {
    state.autoScanDone = true;
    tg.showScanQrPopup({ text: 'Скануйте QR поштомату' }, async (res) => {
      autoScanPopupOpen = false;
      if (!res) {
        if (auto && !autoScanCompleted && !state.branchID) {
          scheduleAutoScanRetry(800);
        }
        return;
      }
      const url = res.data || res;
      log('QR=' + url);
      const success = await resolveLocker(url);
      if (!success && auto && !state.branchID) {
        autoScanCompleted = false;
        scheduleAutoScanRetry(1200);
      }
    });
    autoScanPopupOpen = true;
    return true;
  } catch (e) {
    log('qr:popup_error ' + (e?.message || e));
    autoScanPopupOpen = false;
    if (auto) {
      state.autoScanDone = false;
      toast('Не вдалося автоматично запустити сканер. Спробуйте ще раз.');
      scheduleAutoScanRetry(1200);
    } else {
      toast('Не вдалося запустити сканер. Спробуйте ще раз.');
    }
    return false;
  }
}

window.addEventListener('DOMContentLoaded', async () => {
  // Initialize Lucide icons
  if (typeof lucide !== 'undefined') lucide.createIcons();

  // Контролер звʼязку піднімаємо ПЕРШИМ, ще до перших запитів (налаштування,
  // шаблони проблем): інакше їхні мережеві збої йшли в reportFailure, коли
  // контролера ще не існує, і зникали безслідно — ні перевірки, ні сліду.
  // Сама стартова перевірка (check('load')) лишається нижче, після awaitʼів.
  const connectivityCtrl = initConnectivity();


  ensureWebAppReady();
  ensureWebAppExpanded();
  
  // Check if branchIDRef is passed via URL (from delivery2 postomat flow)
  const urlParams = new URLSearchParams(window.location.search);
  const urlBranchRef = urlParams.get('branchIDRef');
  if (urlBranchRef) {
    // Skip auto-scan, resolve directly by ref
    log('URL branchIDRef=' + urlBranchRef);
    const fakeUrl = 'https://meest.com/' + urlBranchRef;
    const ok = await resolveLocker(fakeUrl);
    if (ok) {
      log('Resolved from URL param');
    }
  } else {
    scheduleAutoScanRetry(180, { force: true });
  }
  window.addEventListener('webapp:settings-applied', (evt) => {
    const detail = evt?.detail;
    if (detail && typeof detail.tsdMode === 'boolean') {
      setTsdMode(detail.tsdMode, { persistRemote: false, showToast: false });
    }
    if (detail && typeof detail.trainingMode === 'boolean' && detail.trainingMode !== lastTrainingMode) {
      lastTrainingMode = detail.trainingMode;
      void handleTrainingToggle(detail.trainingMode, { source: 'settings-sync' }).catch((err) => log('training:sync_fail ' + (err?.message || err)));
    }
  });

  const settings = await loadCourierSettings({ silent: true });
  lastTrainingMode = !!(settings && settings.trainingMode);
  if (settings && typeof settings.tsdMode === 'boolean') {
    setTsdMode(settings.tsdMode, { persistRemote: false, showToast: false });
  }

  initHeaderToggles();
  initTsdToggles();
  initSettingsModal();
  initSimulator();
  await initProblemTemplates();
  if (trainingSettingEnabled()) {
    try {
      await handleTrainingToggle(true, { source: 'initial', initial: true });
    } catch (err) {
      log('training:init_fail ' + (err?.message || err));
    }
  }
  // Toggle test badge depending on backend flag
  const testBadge = document.getElementById('badge_test_mode');
  if (testBadge) {
    testBadge.classList.toggle('hidden', !CONFIG.isTestPudo);
  }
  // Demo mode removed - no longer available via UI
  initTsdKeyboard(
    async (p, code) => {
      // processPickupCode
      if (p) {
        state.currentPickup = p;
        document.getElementById('pickup_actions')?.classList.remove('hidden');
        await pickupOpen();
      }
      await handlePickupCode(code);
    },
    (code) => startDeliveryFor(code),
    (url) => resolveLocker(url),
  );

  const withBtnLoading = (btn, fn) => {
    if (!btn) return Promise.resolve().then(fn);
    btn.classList.add('is-loading');
    btn.disabled = true;
    return Promise.resolve()
      .then(fn)
      .finally(() => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
  };

  document.querySelectorAll('.inventory-action-buttons button[data-result]')?.forEach((btn) => {
    const key = btn.getAttribute('data-result');
    btn.addEventListener('click', () => submitInventoryStatus(key));
  });

  document.getElementById('btn_scan_start')?.addEventListener('click', () => {
    autoScanCompleted = true;
    cancelAutoScanTimer();
    autoScanAttempts = 0;
    scanLocker();
  });

  // Home button (logo) — return to caller page or reset to start screen
  document.getElementById('btn_home')?.addEventListener('click', () => {
    // Вихід від поштомата — опитування звʼязку більше нікому не потрібне.
    stopPostamatOnline('home');
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('returnTo');
    if (returnTo === 'branches') {
      const tgUserId = params.get('tg_user_id') || '';
      const courierMeestId = params.get('courier_meest_id') || '';
      let url = `/branches.html?tg_user_id=${encodeURIComponent(tgUserId)}`;
      if (courierMeestId) url += `&courier_meest_id=${encodeURIComponent(courierMeestId)}`;
      window.location.href = url;
      return;
    }
    if (returnTo === 'delivery2' || returnTo === 'delivery3') {
      const tgUserId = params.get('tg_user_id') || '';
      window.location.href = `/delivery3.html?tg_user_id=${encodeURIComponent(tgUserId)}&screen=branches`;
      return;
    }
    show('screen_start');
  });

  // Telegram BackButton — return to caller page
  if (tg?.BackButton) {
    tg.BackButton.show();
    tg.BackButton.onClick(() => {
      stopPostamatOnline('back');
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get('returnTo');
      if (returnTo === 'branches') {
        const tgUserId = params.get('tg_user_id') || '';
        const courierMeestId = params.get('courier_meest_id') || '';
        let url = `/branches.html?tg_user_id=${encodeURIComponent(tgUserId)}`;
        if (courierMeestId) url += `&courier_meest_id=${encodeURIComponent(courierMeestId)}`;
        window.location.href = url;
        return;
      }
      if (returnTo === 'delivery2' || returnTo === 'delivery3') {
        const tgUserId = params.get('tg_user_id') || '';
        window.location.href = `/delivery3.html?tg_user_id=${encodeURIComponent(tgUserId)}&screen=branches`;
        return;
      }
      // Default: go to start screen or close
      const visible = document.querySelector('.panel:not(.hidden)');
      if (visible?.id === 'screen_start') {
        try { tg.close(); } catch (_) {}
      } else {
        show('screen_start');
      }
    });
  }
  
  document.getElementById('btn_qr_diag')?.addEventListener('click', () => {
    const info = [
      '🔍 Діагностика сканера QR',
      '',
      '📱 Telegram:',
      '  Версія: ' + (tg?.version || 'невідома'),
      '  Платформа: ' + (tg?.platform || 'невідома'),
      '',
      '📷 API сканера:',
      '  window.Telegram: ' + (window.Telegram ? '✅ є' : '❌ відсутній'),
      '  window.Telegram.WebApp: ' + (window.Telegram?.WebApp ? '✅ є' : '❌ відсутній'),
      '  tg.showScanQrPopup: ' + (typeof tg?.showScanQrPopup === 'function' ? '✅ є (функція)' : '❌ відсутній'),
      '  qrSupported: ' + (qrSupported ? '✅ так' : '❌ ні'),
      '  scanInProgress: ' + (scanInProgress ? '⚠️ так (застряг!)' : '✅ ні'),
      '',
      '🔧 Стан:',
      '  Режим ТЗД: ' + (tsdActive() ? '⚠️ увімкнено' : '✅ вимкнено'),
      '  APP_VERSION: ' + APP_VERSION,
      '',
      '💡 Рекомендації:',
      qrSupported ? '✅ Сканер повинен працювати' : '❌ Оновіть Telegram до версії 6.1+',
      scanInProgress ? '⚠️ Скан застряг - спробуйте ще раз (автоскидання)' : '',
      tsdActive() ? '⚠️ Вимкніть режим ТЗД для використання сканера' : '',
    ].filter(Boolean).join('\n');
    
    alert(info);
    
    // Примусово скинути scanInProgress якщо застряг
    if (scanInProgress) {
      log('qr:diag_reset_scanInProgress');
      scanInProgress = false;
      if (scanGuard) {
        clearTimeout(scanGuard);
        scanGuard = null;
      }
    }
  });
  
  document.getElementById('btn_pickup_open')?.addEventListener('click', (e) => withBtnLoading(e.currentTarget, pickupOpen));
  document.getElementById('btn_pickup_resend')?.addEventListener('click', (e) => withBtnLoading(e.currentTarget, pickupResend));
  document.getElementById('btn_pickup_scan')?.addEventListener('click', pickupScanTsdAware);
  document.getElementById('btn_pickup_manual')?.addEventListener('click', pickupManual);
  document.getElementById('btn_pickup_manual_submit')?.addEventListener('click', (e) => withBtnLoading(e.currentTarget, pickupManualSubmit));
  document.getElementById('btn_pickup_skip')?.addEventListener('click', (e) => withBtnLoading(e.currentTarget, pickupSkip));
  document.getElementById('btn_pickup_problem')?.addEventListener('click', showPickupProblem);
  const pickupProblemMenu = document.getElementById('pickup_problem_menu');
  if (pickupProblemMenu) {
    pickupProblemMenu.addEventListener('click', (evt) => {
      const btn = evt.target.closest('button[data-problem-code]');
      if (!btn || !pickupProblemMenu.contains(btn)) return;
      evt.preventDefault();
      const code = btn.getAttribute('data-problem-code');
      if (!code) return;
      const needsNote = btn.dataset.requiresNote === 'true';
      if (needsNote) {
        promptPickupProblemNote(code, true);
        return;
      }
      resetPickupProblemNote();
      withBtnLoading(btn, () => sendProblem(code, null));
    });
  }
  const pickupNoteSubmit = document.getElementById('btn_problem_note_submit');
  if (pickupNoteSubmit) {
    pickupNoteSubmit.addEventListener('click', (e) => {
      const pending = state.pendingPickupProblem;
      if (!pending || !pending.code) {
        toast('Оберіть проблему');
        return;
      }
      const input = document.getElementById('pickup_problem_note');
      const note = (input?.value || '').trim();
      if (pending.requiresNote && !note) {
        toast('Додайте короткий опис');
        input?.focus();
        return;
      }
      withBtnLoading(e.currentTarget, () => sendProblem(pending.code, note || null));
    });
  }
  const pickupNoteInput = document.getElementById('pickup_problem_note');
  pickupNoteInput?.addEventListener('keydown', (evt) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      pickupNoteSubmit?.click();
    }
  });
  document.getElementById('btn_to_delivery')?.addEventListener('click', toDelivery);
  
  // Діагностика сканера перенесена в налаштування (btn_qr_diag_delivery видалено)
  
  // Слухачі фільтрів проблем видалені (функціонал спрощено)
  // Повернення до екрану забору зі списку вкладення
  document.getElementById('btn_delivery_back')?.addEventListener('click', () => {
    // Показати екран забору
    show('screen_pickup');
    // Сховати активні дії вкладення
    document.getElementById('delivery_actions')?.classList.add('hidden');
    resetDeliveryConfirmButton();
    highlightSizeButton(null);
    document.getElementById('delivery_cell_label')?.classList.add('hidden');
    // Порада 13 «змінити розмір» — про посилку вкладення, на заборі їй не місце.
    clearCellResizeWarn();
    // Очистити мета
    clearDeliveryMeta();
  });
  document.getElementById('btn_delivery_scan')?.addEventListener('click', () => {
    if (scanInProgress) return;
    deliveryScan();
  });
  // Кнопка повернення до забору видалена (UX оновлено)
  document.getElementById('btn_pickup_back')?.addEventListener('click', () => {
    // Back from details to list
    document.getElementById('pickup_details')?.classList.add('hidden');
    document.getElementById('pickup_list')?.classList.remove('hidden');
    // Show navigation to delivery again
    document.getElementById('pickup_to_delivery_wrap')?.classList.remove('hidden');
    // Reset panels and problem UI to avoid global leak
    hidePickupProblem();
    resetPickupScanPanel();
  });
  // Кнопки сканування та завершення прибрані з UX; підтвердження тепер основна дія зверху
  document.getElementById('btn_delivery_confirm')?.addEventListener('click', (e) => withBtnLoading(e.currentTarget, deliveryConfirm));
  document.getElementById('btn_delivery_retry')?.addEventListener('click', (e) => withBtnLoading(e.currentTarget, deliveryRetry));
  // Delivery problem menu
  document.getElementById('btn_delivery_problem')?.addEventListener('click', () => {
    const menu = document.getElementById('delivery_problem_menu');
    if (!menu) return;
    if (menu.classList.contains('hidden')) {
      showDeliveryProblemMenu();
    } else {
      hideDeliveryProblemMenu();
    }
  });
  const deliveryProblemMenu = document.getElementById('delivery_problem_menu');
  if (deliveryProblemMenu) {
    deliveryProblemMenu.addEventListener('click', (evt) => {
      const btn = evt.target.closest('button[data-problem-code]');
      if (!btn || !deliveryProblemMenu.contains(btn)) return;
      evt.preventDefault();
      const code = btn.getAttribute('data-problem-code');
      if (!code) return;
      const needsNote = btn.dataset.requiresNote === 'true';
      if (needsNote) {
        promptDeliveryProblemNote(code, true);
        return;
      }
      resetDeliveryProblemNote();
      withBtnLoading(btn, () => submitDeliveryProblem(code, null));
    });
  }
  const deliveryNoteSubmit = document.getElementById('btn_delivery_problem_note_submit');
  if (deliveryNoteSubmit) {
    deliveryNoteSubmit.addEventListener('click', (e) => {
      const pending = state.pendingDeliveryProblem;
      if (!pending || !pending.code) {
        toast('Оберіть проблему');
        return;
      }
      const input = document.getElementById('delivery_problem_note');
      const note = (input?.value || '').trim();
      if (pending.requiresNote && !note) {
        toast('Додайте короткий опис');
        input?.focus();
        return;
      }
      withBtnLoading(e.currentTarget, () => submitDeliveryProblem(pending.code, note || null));
    });
  }
  const deliveryNoteInput = document.getElementById('delivery_problem_note');
  deliveryNoteInput?.addEventListener('keydown', (evt) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      deliveryNoteSubmit?.click();
    }
  });
  // Окремої кнопки «Відкрити комірку більшого розміру» більше немає: вона
  // існувала лише для епіцентрівського флоу, де сітка ховалась. Сітка тепер
  // показується всім, і «більший розмір» — це сусідня літера в ній.
  document.querySelectorAll('#size_selector .size-btn')?.forEach((btn) =>
    btn.addEventListener('click', (e) => {
      const b = e.currentTarget;
      // Розмір із нулем вільних задизейблений розміткою, але клік може прийти
      // і по ньому (клавіатура ТЗД, подія з дочірнього span) — не пускаємо
      // курʼєра у відмову, про яку ми вже знаємо.
      if (b.disabled) return;
      const size = b.getAttribute('data-size') || DEFAULT_CELL_SIZE;
      state.currentCellSize = size;
      highlightSizeButton(size);
      // .finally(renderSizeGrid) обовʼязковий: withBtnLoading у своєму finally
      // БЕЗУМОВНО робить btn.disabled = false і тим скасовує погашення, яке
      // renderSizeGrid щойно поставив на відмову 1С «немає вільних комірок».
      // Без цього кнопка лишалась сірою з підписом «немає», але натискалась —
      // і кожен тап слав ще один гарантовано відмовний courierInsert.
      withBtnLoading(b, attemptDeliveryOpen).finally(() => renderSizeGrid());
    }),
  );
  document.getElementById('btn_close')?.addEventListener('click', () => {
    if (simulator.isActive()) simulator.notify('session-finished');
    stopPostamatOnline('finish');
    finish();
  });
  // Кнопка завершення більше не використовується у вкладенні
  // Error overlays controls
  // Кнопку «Спробувати знову» на блокері звʼязку вішає сам connectivity.js:
  // він спершу перевіряє сервер і перезавантажує сторінку лише коли той ожив.
  //
  // Стартову перевірку робимо ТУТ, а не у слухачі window 'load': цей обробник
  // async, і поки він чекає на перші запити (налаштування, шаблони проблем),
  // подія load встигає пройти — слухач, зареєстрований нижче після await,
  // уже не спрацьовує. Саме тому стара перевірка доступності бекенда на
  // практиці часто не виконувалась зовсім.
  if (connectivityCtrl) {
    connectivityCtrl.check('load');
  } else {
    // Модуль не підвантажився (стара розмітка з кешу або зірваний запит за
    // самим connectivity.js) — старий шлях. showBackendDownOverlay у цьому
    // разі малює чесний текст і сам вішає робочу кнопку: автоперевірки тут
    // немає, і обіцяти її не можна.
    fetch('/webapp/maintenance/status')
      .then(r => r.ok ? r.json() : { active: false })
      .then(st => {
        if (st && st.active) {
          if (!state.operationActive) showMaintenanceOverlay(st.message, st.endsAt);
        } else {
          hideMaintenanceOverlay();
        }
      })
      .catch(() => { showBackendDownOverlay(); });
  }
  document.getElementById('btn_retry_upstream')?.addEventListener('click', () => {
    hideUpstreamErrorOverlay();
    location.reload();
  });

  if (tg?.onEvent) {
    try {
      tg.onEvent('viewportChanged', (event) => {
        if (event?.isStateStable && !state.branchID && !autoScanCompleted) {
          scheduleAutoScanRetry(150, { force: true });
        }
      });
    } catch (e) {
      log('qr:viewport_event_error ' + (e?.message || e));
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !state.branchID && !autoScanCompleted) {
      scheduleAutoScanRetry(200, { force: true });
    }
    // Згорнутий WebApp пригальмовує таймери — «N хв тому» оновлюємо одразу.
    if (document.visibilityState === 'visible') renderPostamatOnlineBanner();
  });
  // Перехід на іншу сторінку (маршрут, delivery3) — таймери опитування гасимо явно.
  window.addEventListener('pagehide', () => stopPostamatOnline('pagehide'));

  window.addEventListener('load', () => {
    // Перевірка звʼязку переїхала вище (див. connectivityCtrl): цей слухач
    // реєструється вже після await-ів і тому спрацьовує не завжди.
    ensureWebAppExpanded();
    if (!autoScanCompleted && !state.branchID) {
      scheduleAutoScanRetry(240);
    }
    // Early identity probe: trigger a lightweight guarded call so 403 is surfaced immediately
    // If identity present, this returns 200 with a list shape (possibly empty) and does nothing
    api('/routes', { noLoader: true }).catch(() => {
      // 403 is handled inside api() by showing a banner+toast; other errors can be ignored here
    });
    try {
      const prev = localStorage.getItem('lastScreen');
      if (prev === 'screen_done') show('screen_done');
    } catch (_) {}
  });

  log('WebApp ' + APP_VERSION + ' loaded (' + location.search + ')');
  // Перевірка підтримки QR API (Telegram 6.0 не підтримує)
  log('tg object: ' + (tg ? 'exists' : 'missing'));
  log('tg.version: ' + (tg?.version || 'unknown'));
  log('tg.platform: ' + (tg?.platform || 'unknown'));
  if (typeof tg?.showScanQrPopup !== 'function') {
    qrSupported = false;
    log('qr:api_missing - showScanQrPopup not a function');
  } else {
    log('qr:api_available - showScanQrPopup is function');
  }
  // Додатково перевірка window.Telegram
  if (!window.Telegram) {
    log('ERROR: window.Telegram is missing!');
  } else if (!window.Telegram.WebApp) {
    log('ERROR: window.Telegram.WebApp is missing!');
  }
});
